import React, {useEffect, useState, useRef} from "react";
import { Helmet } from "react-helmet";

import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';


import "../App.css";
import "../CSS/programmes/AdminPanel.css";

import { ColorModeIcon } from "../context/ColorModeContext.tsx";
import Navbar from "../components/Navbar.tsx";
import CategoryTreeAddRemove from "../components/CategoryTreeAddRemove.tsx";
import CustomCheckbox from "../components/CustomCheckbox.tsx";
import CustomTextField from "../components/CustomTextField.tsx";
import {DefaultButton, SubmitButton} from "../components/Buttons.tsx";
import axios from "axios";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchableSelect from "../components/SearchableSelect.tsx";
import SearchableMultiSelect from "../components/SearchableMultiSelect.tsx";
import CustomSelect from "../components/CustomSelect.tsx";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}
function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tab-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function AdminPanel() {
  const backendUrl = import.meta.env.VITE_REACT_APP_BACKEND_URL;
  const [categories, setCategories] = useState<Map<number, { name:string; shopId:number }>>(new Map());
  const [ottoCategories, setOttoCategories] = useState<[number, string, number, string][]>([]);
  const [eBayCategories, setEBayCategories] = useState<[number, string, number, number | null][]>([]);
  const [hoodCategories, setHoodCategories] = useState<[number, string, number][]>([]);
  const [colors, setColors] = useState<[number, string][]>([]);
  const [woodTypes, setWoodTypes] = useState<[number, string][]>([]);
  const [materials, setMaterials] = useState<[number, string][]>([]);
  const [manufacturerSuppliers, setManufacturerSuppliers] = useState<[number, string, number, string][]>([]);
  const [invalidWords, setInvalidWords] = useState<string []>([]);
  const [manufacturers, setManufacturers] = useState<{ value: string | number; label: string; }[]>([]);
  const [manufacturersNameFromId, setManufacturersNameFromId] = useState<{ [id: number]: string } | null>(null);
  const [marketsAvailabilities, setMarketsAvailabilities] = useState<{ value: string | number; label: string }[]>([]);
  const [manufacturerMarketsAvailabilityConstraints, setManufacturerMarketsAvailabilityConstraints] = useState<{
    [manufacturerId: string]: { label: string; value: string | number; }[]
  }>({});
  const [listingCreatorFields, setListingCreatorFields] = useState([]);
  const [activeCountries, setActiveCountries] = useState<[number, string][]>([]);
  const [backofficeActiveCountries, setBackofficeActiveCountries] = useState<[number, string][]>([]);
  const [missingMails, setMissingMails] = useState<[number, string][]>([]);
  const [tiefeLaengeMap, setTiefeLaengeMap] = useState<Map<number, string>>(new Map());

  const fetchData = async () => {
      await axios.get(`${backendUrl}/admin_panel/get_db_data/`, config)
        .then(response => {
          const resp = response.data;
          const transformedCategories = new Map<number, { name: string; shopId: number }>(resp.categories.map((cat:{id:number, name:string, path:string, shop_id: number}) => [cat.id, { name: String(cat.name), shopId: Number(cat.shop_id) }]));
          setCategories(transformedCategories);
          const transformedOttoCategories = Object.entries(resp.otto_categories).map(([key, value]: [string, {plenty_cat_name: string; otto_cat_id: number; otto_cat_name: string}]) => [Number(key), value.plenty_cat_name, value.otto_cat_id, value.otto_cat_name] as [number, string, number, string]);
          setOttoCategories(transformedOttoCategories);
          const transformedEbayCategories = Object.entries(resp.ebay_categories).map(([key, value]: [string, {plenty_cat_name: string; ebay_category_id_1: number; ebay_category_id_2: number}]) => [Number(key), value.plenty_cat_name, value.ebay_category_id_1, value.ebay_category_id_2] as [number, string, number, number]);
          setEBayCategories(transformedEbayCategories);
          const transformedHoodCategories = Object.entries(resp.hood_categories).map(([key, value]: [string, {plenty_cat_name: string; hood_category_id: number;}]) => [Number(key), value.plenty_cat_name, value.hood_category_id] as [number, string, number]);
          setHoodCategories(transformedHoodCategories);
          setColors(resp.colors);
          setWoodTypes(resp.wood_types);
          setMaterials(resp.materials);
          setManufacturerSuppliers(resp.manufacturer_supplier);
          setInvalidWords(resp.invalid_words);
          const formattedManufacturers = resp.manufacturer_supplier.map((item) => ({
            label: item[1] as string,
            value: Number(item[0])
          }));
          setManufacturers(formattedManufacturers);
          const formattedManufacturersNameFromId = Object.fromEntries(resp.manufacturer_supplier.map((x) => [x[0], x[1]]));
          setManufacturersNameFromId(formattedManufacturersNameFromId);
          const formattedMarketsAvailabilities = Object.entries(resp.marketsAvailabilities).map(([label, value]) => ({
            label: label,
            value: value as string | number
          }));
          setMarketsAvailabilities(formattedMarketsAvailabilities);
          setManufacturerMarketsAvailabilityConstraints(resp.manufacturerMarketsAvailabilityConstraints);
          setListingCreatorFields(resp.ListingCreatorFields);
          setActiveCountries(resp.active_countries);
          setBackofficeActiveCountries(resp.backoffice_active_countries);
          setMissingMails(resp.missing_mails);
          const parsedTiefeLaenge = new Map<number, string>(
            Object.entries(resp.tiefe_laenge_data || {}).map(([k, v]) => [Number(k), String(v)])
          );
          setTiefeLaengeMap(parsedTiefeLaenge);
        })
        .catch(error => alert("Fehler beim Laden der benötigten Informationen. Lade die Seite bitte erneut." + error.toString()));
    };

  useEffect(() => {
    fetchData();
  }, []);

  const config = {headers: {"Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}`,},};

  // States for mandatory information
  const [selectedTab, setSelectedTab] = useState<number>(0);

  const ebay_ref = useRef(null);
  const otto_ref = useRef(null)
  const hood_ref = useRef(null)
  const invalid_words_ref = useRef(null)
  const color_ref = useRef(null);
  const woodType_ref = useRef(null);
  const material_ref = useRef(null);
  const manufacturerSupplier_ref = useRef(null);
  const manufacturerAvailabilityConstraint_ref = useRef(null);
  const rolesApplications_ref = useRef(null);
  const userRoles_ref = useRef(null);
  const ListingCreator_ref = useRef(null);
  const AktiveLaender_ref = useRef(null);
  const BackofficeAktiveLaender_ref = useRef(null);
  const MissingMails_ref = useRef(null);


  const renderErrorMessage = (errorMessage) => {
    if (errorMessage) {
      return (
        <div className="error-message-container">
          {" "}
          <p>{errorMessage}</p>{" "}
        </div>
      );
    } else {
      return null;
    }
  };
  const renderInfoMessage = (infoMessage) => {
    if (infoMessage) {
      return (
        <div className="info-message-container">
          {" "}
          <p>{infoMessage}</p>{" "}
        </div>
      );
    } else {
      return null;
    }
  };

  const renderScrollDownButton = (target: React.MutableRefObject<HTMLElement | null>) =>{
    return (<DefaultButton label={"Springe zu Möglichkeit einen Eintrag hinzuzufügen"}
                           size="small"
                           sx={{ minWidth: "90px", width: "70%", height: "30px"}}
                           variant="outlined"
                           startIcon={<KeyboardArrowDownIcon />}
                           endIcon={<KeyboardArrowDownIcon />}
                           onClick={() => {target.current?.scrollIntoView({ behavior: "smooth" });}}
        />
    )
  }

  // Tab 0
  const RenderUserPanel = () => {
  const [errorMessageRole, setErrorMessageRole] = useState<string | null>(null);
  const [infoMessageRole, setInfoMessageRole] = useState<string | null>(null);
  const [errorMessageUser, setErrorMessageUser] = useState<string | null>(null);
  const [infoMessageUser, setInfoMessageUser] = useState<string | null>(null);
  const [apps, setApps] = useState<string[]>([]);
  const [rolesApplications, setRolesApplications] = useState<{[app:string]: string[]}>({});
  const [users, setUsers] = useState<[string, string] []>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [newRole, setNewRole] = useState<string>("");
  const [newRoleApplications, setNewRoleApplications] = useState<string[]>([]);
  const [newUserName, setNewUserName] = useState<string>("");
  const [newUserRole, setNewUserRole] = useState<string>("");

  useEffect(() => {
    getRoles();
    getUsers();
    getApps();
    getRolesApplications();
  }, []);

  const getRoles = async () => {
    await axios.get(`${backendUrl}/admin_panel/get_roles/`, config)
      .then(response => {
        setRoles(response.data);
      })
      .catch(error => alert("Fehler beim Laden der Benutzer " + error.toString()))
  };
  const getUsers = async () => {
    await axios.get(`${backendUrl}/admin_panel/get_users/`, config)
      .then(response => {
        setUsers(response.data);
      })
      .catch(error => alert("Fehler beim Laden der Benutzer " + error.toString()))
  };
  const getApps = async () => {
    await axios.get(`${backendUrl}/admin_panel/get_apps/`, config)
      .then(response => {
        setApps(response.data);
      })
      .catch(error => alert("Fehler beim Laden der Apps " + error.toString()))
  };
  const getRolesApplications = async () => {
    await axios.get(`${backendUrl}/admin_panel/get_roles_applications/`, config)
      .then(response => {
        setRolesApplications(response.data);
      })
      .catch(error => alert("Fehler beim Laden der Rollen " + error.toString()))
  };


  const handleAddRoleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setErrorMessageRole("");
    setInfoMessageRole("");

    if (!newRole || !newRoleApplications) {
      setErrorMessageRole("Alle Felder müssen ausgefüllt sein!");
      return;
    } else if (newRoleApplications.length == 0) {
      setErrorMessageRole("Wähle mindestens eine Anwendung für die neue Rolle!");
      return;
    }

    const data = {
      role: newRole,
      applications: newRoleApplications
    };
    await axios
      .post(
        `${backendUrl}/admin_panel/add_role_and_role_applications/`,
        data,
        config
      )
      .then(() => {
        setInfoMessageRole("Das Hinzufügen der Zuordnung war erfolgreich.");
        getRolesApplications();
        getRoles();
        setNewRole("");
        setNewRoleApplications([]);
      })
      .catch((err) => {
        const error_str = (!err.response) ? "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar." // This will handle network errors or cases where the server doesn't respond at all
          : (err.response.status === 500) ? "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan)."
            : err.response.data?.detail?.toString();
        setErrorMessageRole(error_str);
      });
  };

  const handleDeleteRoleClick = async (event: React.MouseEvent<HTMLButtonElement>, data) => {
    event.preventDefault();
    setErrorMessageRole("");

    await axios
      .delete(`${backendUrl}/admin_panel/remove_role_and_roles_applications/`, {
        ...config,
        data: data
      })
      .then(() => {
        getRolesApplications();
        getRoles();
        setInfoMessageRole("Eintrag erfolgreich gelöscht.");
      })
      .catch((err) => {
        const error_str = (!err.response)
          ? "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar."
          : (err.response.status === 500)
            ? "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan)."
            : err.response.data?.detail?.toString();
        setErrorMessageRole(error_str);
      });
  }

  const handleAddUserClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setErrorMessageUser("");
    setInfoMessageUser("");

    if (!newUserName || !newUserRole) {
      setErrorMessageUser("Alle Felder müssen ausgefüllt sein!");
      return;
    }

    const data = {
      user: newUserName,
      role: newUserRole
    };
    await axios
      .post(
        `${backendUrl}/admin_panel/add_user/`,
        data,
        config
      )
      .then(() => {
        setInfoMessageUser("Das Hinzufügen der Zuordnung war erfolgreich.");
        getUsers();
        setNewUserName("");
        setNewUserRole("");
      })
      .catch((err) => {
        const error_str = (!err.response) ? "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar." // This will handle network errors or cases where the server doesn't respond at all
          : (err.response.status === 500) ? "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan)."
            : err.response.data?.detail?.toString();
        setErrorMessageUser(error_str);
      });
  };

  const handleDeleteUserClick = async (event: React.MouseEvent<HTMLButtonElement>, data) => {
    event.preventDefault();
    setErrorMessageUser("");

    await axios
      .delete(`${backendUrl}/admin_panel/remove_user/`, {
        ...config,
        data: data
      })
      .then(() => {
        getUsers();
        setInfoMessageUser("Eintrag erfolgreich gelöscht.");
      })
      .catch((err) => {
        const error_str = (!err.response)
          ? "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar."
          : (err.response.status === 500)
            ? "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan)."
            : err.response.data?.detail?.toString();
        setErrorMessageUser(error_str);
      });
  }

  return <div style={{display: 'flex', flexDirection: 'column', gap: "15px", alignItems: 'center'}}>
    <p>Hier kannst du neue Rollen und Benutzer hinzufügen</p>
    <p>Was ist eine Rolle? Eine Rolle fasst eine Benutzergruppe zusammen und gibt ihr die Rechte, gewisse Programme zu nutzen.</p>
    <p>z.B. wenn Personen nur Artikel und Listings erstellen können sollen. Dann erstelle eine Rolle mit diesen beiden Apps. Diese kannst du den Benutzern dann zuweisen. </p>
    <p>Die Benutzernamen müssen denen von Plentymarkets entsprechen!</p>
    {renderScrollDownButton(rolesApplications_ref)}
    <div style={{display: "flex", flexDirection: "row", justifyContent: "center"}}>
      <table style={{width: "100%", borderCollapse: "collapse", marginTop: "10px"}}>
        <thead>
        <tr style={{borderBottom: "2px solid #ccc", textAlign: "center"}}>
          <th style={{padding: "10px", width: "25%"}}>Rolle</th>
          <th style={{padding: "10px", width: "50%"}}>Anwendungen</th>
          <th style={{padding: "10px", width: "5%"}}></th>
        </tr>
        </thead>
        <tbody>
        {rolesApplications && Object.entries(rolesApplications).length > 0 && Object.entries(rolesApplications).map(([role, role_apps], idx) => (
          <tr key={idx} style={{borderBottom: "1px solid #eee"}}>
            <td style={{padding: "10px"}}>{role}</td>
            <td style={{padding: "10px"}}>
              <SearchableMultiSelect items={apps.map(app => ({ value: app, label: app }) )} inputLabel="Apps" disabled={true}
                                     value={role_apps.map(app => ({ value: app, label: app }) )} setValue={() => {}} selectAllOption={false}/>
            </td>
            <td style={{padding: "10px"}}>
              {role != "admin" && <DefaultButton
                label="-"
                color={"red"}
                variant={"contained"}
                size="small"
                sx={{minWidth: "30px", width: "30px", height: "30px"}}
                onClick={async (e) => {
                  await handleDeleteRoleClick(e, {role: role})
                }}
              />}
            </td>
          </tr>
        ))}
        </tbody>
      </table>
    </div>
    <div style={{display: 'flex', flexDirection: 'row', gap: "15px", justifyContent: "center", width: "75%"}}>
      <CustomTextField label={"Rolle"} value={newRole} onChange={(e) => setNewRole(e.target.value.toLowerCase())} is_required={true} maxLength={20}/>
      <SearchableMultiSelect items={apps.map(app => ({ value: app, label: app }))} inputLabel="Apps" selectAllOption={false}
                             value={newRoleApplications.map(roleApp => ({  value: roleApp, label: roleApp}))}
                             setValue={(newVal) => setNewRoleApplications(newVal.map(obj => obj.value))}/>
    </div>
    <div style={{display: 'flex', flexDirection: 'column', gap: "15px", alignItems: 'center', width: '100%'}}
         ref={rolesApplications_ref}>
      <SubmitButton label="Rolle hinzufügen" size="small" onClick={handleAddRoleClick} sx={{width: "50%"}}/>
      {renderErrorMessage(errorMessageRole)}
      {renderInfoMessage(infoMessageRole)}
    </div>
    {renderScrollDownButton(userRoles_ref)}
    <div style={{display: "flex", flexDirection: "row", justifyContent: "center", width: "50%"}}>
      <table style={{width: "100%", borderCollapse: "collapse", marginTop: "10px"}}>
        <thead>
        <tr style={{borderBottom: "2px solid #ccc", textAlign: "center"}}>
          <th style={{padding: "10px", width: "35%"}}>Benutzer</th>
          <th style={{padding: "10px", width: "35%"}}>Rolle</th>
          <th style={{padding: "10px", width: "5%"}}></th>
        </tr>
        </thead>
        <tbody>
        {users && Object.entries(users).length > 0 && users.map((entry, idx) => (
          <tr key={idx} style={{borderBottom: "1px solid #eee"}}>
            <td style={{padding: "10px"}}>{entry[0]}</td>
            <td style={{padding: "10px"}}>{entry[1]}</td>
            <td style={{padding: "10px"}}>
              <DefaultButton
                  label="-"
                  color={"red"}
                  variant={"contained"}
                  size="small"
                  sx={{minWidth: "30px", width: "30px", height: "30px"}}
                  onClick={async (e) => {
                    await handleDeleteUserClick(e, {user: entry[0]})
                  }}
              />
            </td>
          </tr>
        ))}
        </tbody>
      </table>
    </div>
    <div style={{display: 'flex', flexDirection: 'row', gap: "15px", justifyContent: "center", width: "75%"}}>
      <CustomTextField label={"Plenty Nutzername"} value={newUserName} onChange={(e) => setNewUserName(e.target.value.toLowerCase())} is_required={true} maxLength={20}/>
      <SearchableSelect inputLabel={"Rolle"}
                        items={roles.map(r => ({ value:r, label:r }))}
                        value={({ value: newUserRole, label:newUserRole })}
                        setValue={(newVal) => setNewUserRole(newVal.value)} is_required={true} sx={{width: "50%"}}/>
    </div>
    <div style={{display: 'flex', flexDirection: 'column', gap: "15px", alignItems: 'center', width: '100%'}}
         ref={userRoles_ref}>
      <SubmitButton label="Benutzer hinzufügen" size="small" onClick={handleAddUserClick} sx={{width: "50%"}}/>
      {renderErrorMessage(errorMessageUser)}
      {renderInfoMessage(infoMessageUser)}
    </div>
  </div>
};
  // Tab 1
  const RenderManageCategoriesPanel = (refreshGlobalData: () => void) => {
    // --- Shared State ---
    // We use this to force both trees to reload when an add or remove happens
    const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

    // --- ADD Category State ---
    const [isMainCategory, setIsMainCategory] = useState<boolean>(false);
    const [newCat, setNewCat] = useState<string>("");
    const [newCatId, setNewCatId] = useState<number | "">(""); // changed init to empty string for clean input
    const [newCatShop, setNewCatShop] = useState<number>(10159); // Default to Natur24
    const [addSelectedCategories, setAddSelectedCategories] = useState<Map<number, string>>(new Map());
    const [addErrorMessage, setAddErrorMessage] = useState<string | null>(null);
    const [addInfoMessage, setAddInfoMessage] = useState<string | null>(null);

    // --- REMOVE Category State ---
    const [removeSelectedCategories, setRemoveSelectedCategories] = useState<Map<number, string>>(new Map());
    const [removeErrorMessage, setRemoveErrorMessage] = useState<string | null>(null);
    const [removeInfoMessage, setRemoveInfoMessage] = useState<string | null>(null);

    // --- CHANGE Category Name ---
    const [renameNewName, setRenameNewName] = useState<string>("");
    const [renameNewShop, setRenameNewShop] = useState<number>(10159);

    // --- Länge/Tiefe ---
    const [renameTiefeLaenge, setRenameTiefeLaenge] = useState<string>("none");

    // Helper to get config (ensures token is fresh)
    const getAxiosConfig = () => ({
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });

    // --- ADD Logic ---
    useEffect(() => {
      if (addSelectedCategories.size > 1) {
        setAddErrorMessage("Du hast mehr als eine Kategorie ausgewählt.");
      } else if (addSelectedCategories.size === 0 && !isMainCategory) {
        setAddErrorMessage("Wähle bitte eine Kategorie aus.");
      } else {
        setAddErrorMessage("");
      }
    }, [addSelectedCategories, isMainCategory]);

    const handleAddCategoryClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setAddErrorMessage(null);

      // Regex Cleanup
      const newCategoryName = newCat
        .replace(/\s+/g, "")
        .replace(/-\/+/g, "_")
        .replace(/[^a-zA-Z0-9_]+/g, "_")
        .replace(/^_+|_+$/g, "");

      let newCatPath = null;
      if (isMainCategory) {
        newCatPath = newCategoryName;
      } else {
        const selCatPath = addSelectedCategories.get(Array.from(addSelectedCategories.keys())[0]);
        newCatPath = selCatPath + "." + newCategoryName;
      }

      const data = {
        new_cat_id: newCatId,
        new_cat_path: newCatPath,
        new_cat_name: newCat,
        shop_id: newCatShop
      };

      try {
        const res = await axios.put(
          `${backendUrl}/admin_panel/add_category/`,
          data,
          getAxiosConfig()
        );
        setAddInfoMessage(res.data);
        // alert(res.data);
        // Update shared trigger to reload BOTH trees
        setRefreshTrigger((prev) => prev + 1);
        // Reset inputs
        setNewCat("");
        setNewCatId("");
      } catch (err: any) {
        handleError(err, setAddErrorMessage);
      }
    };

    // --- REMOVE Logic ---
    useEffect(() => {
      if (removeSelectedCategories.size > 1) {
        setRemoveErrorMessage("Du hast mehr als eine Kategorie ausgewählt.");
      } else if (removeSelectedCategories.size === 1) {
        const catId = Array.from(removeSelectedCategories.keys())[0];
        const catData = categories.get(catId);

        if (catData) {
          setRenameNewName(catData.name);
          setRenameNewShop(catData.shopId);
          setRenameTiefeLaenge(tiefeLaengeMap.get(catId) || "none");
        }
      } else if (removeSelectedCategories.size === 0) {
        setRemoveErrorMessage("Wähle bitte eine Kategorie aus.");
      } else {
        setRemoveErrorMessage("");
      }
    }, [removeSelectedCategories, categories, tiefeLaengeMap]);

    const handleRemoveCategoryClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setRemoveErrorMessage(null);
      const cat_id = Array.from(removeSelectedCategories.keys())[0];
      const cat_path = removeSelectedCategories.get(cat_id);

      try {
        const res = await axios.delete(
          `${backendUrl}/admin_panel/remove_category/${cat_id}/${cat_path}/`,
          getAxiosConfig()
        );
        setRemoveInfoMessage(res.data);
        // alert(res.data);
        // Update shared trigger to reload BOTH trees
        setRefreshTrigger((prev) => prev + 1);
        // Clear selection
        setRemoveSelectedCategories(new Map());
      } catch (err: any) {
        handleError(err, setRemoveErrorMessage);
      }
    };

    // --- RENAME Logic ---
    const handleChangeNameClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setRemoveErrorMessage(null);

      if (!renameNewName) {
          setRemoveErrorMessage("Bitte gib einen neuen Namen ein.");
          return;
      }

      const cat_id = Array.from(removeSelectedCategories.keys())[0];
      // Path is sent just for logging or identification, though we won't change it in DB
      const cat_path = removeSelectedCategories.get(cat_id);

      const data = {
          cat_id: cat_id,
          cat_path: cat_path,
          new_cat_name: renameNewName,
          shop_id: renameNewShop
      };

      try {
        const res = await axios.put(`${backendUrl}/admin_panel/change_category_name/`, data, getAxiosConfig());
        console.log(res.data.detail);
        setRefreshTrigger((prev) => prev + 1);
        refreshGlobalData();
        setRenameNewName("");
        setRemoveSelectedCategories(new Map());
        setRemoveInfoMessage("Name wurde erfolgreich geändert!");
      } catch (err: any) {
        handleError(err, setRemoveErrorMessage);
      }
    };

    // Change or Remove Tiefe Länge
    const handleSetTiefeLaengeClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setRemoveErrorMessage(null);
      const cat_id = Array.from(removeSelectedCategories.keys())[0];

      try {
        const res = await axios.post(`${backendUrl}/admin_panel/set_tiefe_oder_laenge/`, {
          cat_id: cat_id,
          tiefeoderlaenge: renameTiefeLaenge
        }, getAxiosConfig());

        alert(res.data.detail);
        setRefreshTrigger((prev) => prev + 1);
        refreshGlobalData();
      } catch (err: any) {
        handleError(err, setRemoveErrorMessage);
      }
    };


    // Shared Error Handler
    const handleError = (err: any, setErrorState: (msg: string) => void) => {
      let error_str = "";
      if (!err.response) {
        error_str = "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar.";
      } else if (err.response.status === 500) {
        error_str = "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan).";
      } else {
        error_str = err.response.data.detail ? err.response.data.detail.toString() : "Unbekannter Fehler";
      }
      setErrorState(error_str);
      alert(error_str);
    };

    // Display logic for Add
    const addSelectedId = addSelectedCategories.size === 1 ? Array.from(addSelectedCategories.keys())[0] : null;
    const addSelectedName = addSelectedId !== null ? categories.get(addSelectedId)?.name : null;
    const addSelectionDisplay = addSelectedId !== null
      ? `Ausgewählte übergeordnete Kategorie: ${addSelectedName} (ID: ${addSelectedId})`
      : ""; // Leaves it blank if nothing is selected, exactly like your old code

    // Display logic for Rename and Remove
    const removeSelectedId = removeSelectedCategories.size === 1 ? Array.from(removeSelectedCategories.keys())[0] : null;
    const removeSelectedName = removeSelectedId !== null ? categories.get(removeSelectedId)?.name : null;
    const removeSelectionDisplay = removeSelectedId !== null
      ? `Ausgewählt: ${removeSelectedName} (ID: ${removeSelectedId})`
      : "Keine Kategorie ausgewählt";

    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        gap: "30px",
        paddingBottom: "50px",
        width: "100%",
        justifyContent: "center",
        alignItems: "center"
      }}>

        {/* ================= ADD SECTION ================= */}
        <div style={{display: "flex", flexDirection: "column", justifyContent: "center", width: "80%"}}>
          <h3>Füge eine Kategorie zur internen Datenbank hinzu! (Ändert keine Kategorien in Plentymarkets)</h3>
          <p>Wähle eine Kategorie aus, in die deine eingegebene Kategorie soll. </p>
          <p>Wählst du z.B. "Wohnzimmer.Leuchten" aus, wird deine Kategorie in Wohnzimmer.Leuchten landen.</p>

          <div style={{display: "flex", flexDirection: "row", gap: "15px", marginLeft: "50px", marginRight: "50px"}}>
            <CustomTextField
              label="Kategorien name"
              placeholder="Kategorien name"
              is_required={true}
              errorText="Dieses Feld muss ausgefüllt sein"
              value={newCat}
              onChange={(e) => setNewCat(e.target.value)}
              inputMode={"text"}
            />
            <CustomTextField
              label="Kategorien ID"
              placeholder="Kategorien ID"
              is_required={true}
              errorText="Dieses Feld muss ausgefüllt sein"
              value={newCatId}
              onChange={(e) => setNewCatId(Number(e.target.value))}
              inputMode={"integer"}
            />
            <CustomSelect
              label="Shop"
              inputLabel="Shop"
              items={[
                {value: 10159, label: "Natur24"},
                {value: 73461, label: "Gartengarten24"}
              ]}
              value={newCatShop}
              setValue={(val) => setNewCatShop(Number(val))}
              sx={{width: "250px"}}
            />
          </div>

          <div style={{display: "flex", flexDirection: "column", gap: "15px", marginLeft: "50px", marginRight: "50px"}}>
            <div style={{alignContent: "center"}}>
              <CustomCheckbox
                label={"Als Hauptkategorie hinzufügen"}
                value={isMainCategory}
                onChange={setIsMainCategory}
                is_disabled={false}
              />
            </div>

            {isMainCategory && "Die eingegebene Kategorie wird als Hauptkategorie (z.B. Wohnzimmer, Küche, Schlafzimmer, ...) hinzugefügt."}

            {!isMainCategory && (
              <div>
                <CategoryTreeAddRemove
                  key={`add-${refreshTrigger}`} // Unique key ensures re-render on update
                  singleSelection={true}
                  selectedItems={addSelectedCategories}
                  onSelectedItemsChange={(newSelection) => setAddSelectedCategories(newSelection)}
                />
                <div style={{textAlign: "left", marginTop: "15px"}}>
                  {addSelectionDisplay}
                </div>
              </div>
            )}

            <SubmitButton
              label="Kategorie hinzufügen"
              size="small"
              isDisabled={!isMainCategory && !(addSelectedCategories.size === 1)}
              onClick={handleAddCategoryClick}
            />
          </div>
          {renderErrorMessage(addErrorMessage)}
          {renderInfoMessage(addInfoMessage)}
        </div>

        {/* ================= DIVIDER ================= */}
        <hr style={{width: "80%", border: "0", borderTop: "2px solid #eee", margin: "20px 50px"}}/>

        {/* REMOVE & RENAME SECTION */}
        <div style={{display: "flex", flexDirection: "column", justifyContent: "center", width: "80%"}}>
          <h3>Kategorie bearbeiten oder entfernen</h3>
          <p>Wähle eine Kategorie aus dem Baum aus, um sie umzubenennen oder zu löschen.</p>

          <div style={{display: "flex", flexDirection: "column", marginLeft: "50px", marginRight: "50px", gap: "5px"}}>
            <CategoryTreeAddRemove
              key={`remove-${refreshTrigger}`}
              singleSelection={true}
              selectedItems={removeSelectedCategories}
              onSelectedItemsChange={(newSelection) => setRemoveSelectedCategories(newSelection)}
            />

            <div style={{textAlign: "left", marginTop: "15px", fontWeight: "bold"}}>{removeSelectionDisplay}</div>

            {/* RENAME BOX */}
            <div style={{
              display: "flex",
              flexDirection: "row",
              gap: "15px",
              alignItems: "center",
              backgroundColor: "rgba(0,0,0,0.05)",
              paddingLeft: "15px",
              paddingRight: "15px",
              paddingTop: "15px",
              borderRadius: "8px"
            }}>
              <CustomTextField
                label="Neuer Kategorienname"
                placeholder="Gib den neuen Namen ein"
                value={renameNewName}
                onChange={(e) => setRenameNewName(e.target.value)}
                sx={{flexGrow: 1}}
              />
              <CustomSelect
                label="Shop ändern"
                inputLabel="Shop ändern"
                items={[
                  {value: 10159, label: "Natur24"},
                  {value: 73461, label: "Gartengarten24"}
                ]}
                value={renameNewShop}
                setValue={(val) => setRenameNewShop(Number(val))}
                sx={{width: "200px"}}
              />
              <SubmitButton
                label="Umbenennen"
                size="small"
                isDisabled={removeSelectedCategories.size !== 1 || !renameNewName}
                onClick={handleChangeNameClick}
              />
            </div>

            <hr style={{border: "0", borderTop: "1px solid #ddd"}}/>

            {/* REMOVE BUTTON */}
            <DefaultButton
              label="Kategorie unwiderruflich löschen"
              size="small"
              color="red"
              isDisabled={removeSelectedCategories.size !== 1}
              onClick={handleRemoveCategoryClick}
              sx={{backgroundColor: "#d32f2f", "&:hover": {backgroundColor: "#b71c1c"}}}
            />
          </div>
          {renderErrorMessage(removeErrorMessage)}
          {renderInfoMessage(removeInfoMessage)}
        </div>

        {/* ================= DIVIDER ================= */}
        <hr style={{width: "80%", border: "0", borderTop: "2px solid #eee", margin: "20px 50px"}}/>

        {/* ================= Tiefe Laenge Update or Removal ================= */}
        <div style={{display: "flex", flexDirection: "column", justifyContent: "center", width: "80%"}}>
          <h3>Kategorie mit Tiefe/Länge verknüpfen</h3>
          <p>Wähle eine Kategorie aus dem Baum aus, um Tiefe/Länge hinzuzufügen, die bestehende Angabe zu ändern oder sie zu löschen.</p>

          <div style={{display: "flex", flexDirection: "column", marginLeft: "50px", marginRight: "50px", gap: "5px"}}>

            <CategoryTreeAddRemove
              key={`remove-${refreshTrigger}`}
              singleSelection={true}
              selectedItems={removeSelectedCategories}
              onSelectedItemsChange={(newSelection) => setRemoveSelectedCategories(newSelection)}
              tiefeLaengeMap={tiefeLaengeMap}
            />

            <div style={{
              textAlign: "left",
              marginTop: "15px",
              marginBottom: "5px",
              fontWeight: "bold"
            }}>{removeSelectionDisplay}</div>

            <div style={{
              display: "flex",
              flexDirection: "row",
              gap: "15px",
              alignItems: "center",
              backgroundColor: "rgba(0,0,0,0.05)",
              paddingLeft: "15px",
              paddingRight: "15px",
              paddingTop: "15px",
              paddingBottom: "15px", // Added for symmetry
              borderRadius: "8px",
              marginTop: "10px"
            }}>
              <CustomSelect
                label="Tiefe oder Länge festlegen"
                inputLabel="Maß-Vorgabe"
                items={[
                  {value: "none", label: "Keine Vorgabe (Standard: Tiefe)"},
                  {value: "Tiefe", label: "Tiefe"},
                  {value: "Laenge", label: "Länge"}
                ]}
                value={renameTiefeLaenge}
                setValue={(val) => setRenameTiefeLaenge(String(val))}
                sx={{flexGrow: 1}}
              />
              <SubmitButton
                label="Maß-Vorgabe Speichern"
                size="small"
                isDisabled={removeSelectedCategories.size !== 1}
                onClick={handleSetTiefeLaengeClick}
              />
            </div>
          </div>
          {renderErrorMessage(removeErrorMessage)}
          {renderInfoMessage(removeInfoMessage)}
        </div>
      </div>
    );
  };
  // Tab 2 Combined Tab (Otto, Ebay, Hood)
  const RenderMarketplaceCategoriesPanel = () => {

    // ==========================================
    // =============== OTTO STATE ===============
    // ==========================================
    const [ottoErrorMessage, setOttoErrorMessage] = useState<string | null>(null);
    const [ottoInfoMessage, setOttoInfoMessage] = useState<string | null>(null);
    const [ottoPlentyID, setOttoPlentyID] = useState<string>("");
    const [ottoPlentyName, setOttoPlentyName] = useState<string>("");
    const [ottoID, setOttoID] = useState<string>(""); // Changed to string to handle empty input cleanly, cast to number later
    const [ottoName, setOttoName] = useState<string>("");

    // ==========================================
    // =============== EBAY STATE ===============
    // ==========================================
    const [ebayErrorMessage, setEbayErrorMessage] = useState<string | null>(null);
    const [ebayInfoMessage, setEbayInfoMessage] = useState<string | null>(null);
    const [ebayPlentyID, setEbayPlentyID] = useState<string>("");
    const [ebayPlentyName, setEbayPlentyName] = useState<string>("");
    const [eBayID1, setEBayID1] = useState<string>("");
    const [eBayID2, setEBayID2] = useState<string>("");

    // ==========================================
    // =============== HOOD STATE ===============
    // ==========================================
    const [hoodErrorMessage, setHoodErrorMessage] = useState<string | null>(null);
    const [hoodInfoMessage, setHoodInfoMessage] = useState<string | null>(null);
    const [hoodPlentyID, setHoodPlentyID] = useState<string>("");
    const [hoodPlentyName, setHoodPlentyName] = useState<string>("");
    const [hoodID, setHoodID] = useState<string>("");

    // Derive Maps for the Coverage Tree
    const currentOttoMap = new Map(ottoCategories.map(item => [item[0], { id: item[2], name: item[3] }]));
    const currentEbayMap = new Map(eBayCategories.map(item => [item[0], { id1: item[2], id2: item[3] }]));
    const currentHoodMap = new Map(hoodCategories.map(item => [item[0], { id: item[2] }]));
    // Create an empty state for the read-only tree (so clicks don't break anything)
    const readOnlySelection = new Map<number, string>();

    // ==========================================
    // ============== OTTO LOGIC ================
    // ==========================================
    const getOttoCategories = async () => {
      await axios.get(`${backendUrl}/general/get_otto_categories/`, config)
        .then(response => {
          const responseDict = response.data;
          const transformedOttoCategories = Object.entries(responseDict).map(([key, value]: [string, {
            plenty_cat_name: string;
            otto_cat_id: number;
            otto_cat_name: string
          }]) => [Number(key), value.plenty_cat_name, value.otto_cat_id, value.otto_cat_name] as [number, string, number, string]);
          setOttoCategories(transformedOttoCategories);
        })
        .catch(error => alert("Fehler beim Laden der Otto Kategorien " + error.toString()))
    };

    const handleOttoAddClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setOttoErrorMessage("");
      setOttoInfoMessage("");

      if (!ottoPlentyName) {
        setOttoErrorMessage("Es muss eine gültige Plenty Kategorien ID eingegeben werden.");
        return;
      } else if (!ottoPlentyID || !ottoID || !ottoName) {
        setOttoErrorMessage("Alle Felder müssen ausgefüllt werden!");
        return;
      }

      const data = {
        plenty_id: Number(ottoPlentyID),
        otto_id: Number(ottoID),
        otto_name: ottoName
      };

      await axios.post(`${backendUrl}/admin_panel/add_otto_category/`, data, config)
        .then(() => {
          setOttoInfoMessage("Das Hinzufügen der Zuordnung war erfolgreich.");
          getOttoCategories();
          // Reset fields
          setOttoPlentyID("");
          setOttoPlentyName("");
          setOttoID("");
          setOttoName("");
        })
        .catch((err) => {
          handleError(err, setOttoErrorMessage);
        });
    };

    // ==========================================
    // ============== EBAY LOGIC ================
    // ==========================================
    const getEBayCategories = async () => {
      await axios.get(`${backendUrl}/general/get_ebay_categories/`, config)
        .then(response => {
          const responseDict = response.data;
          const transformedEBayCategories = Object.entries(responseDict).map(([key, value]: [string, {
            plenty_cat_name: string;
            ebay_category_id_1: number;
            ebay_category_id_2: number | null
          }]) => [Number(key), value.plenty_cat_name, value.ebay_category_id_1, value.ebay_category_id_2] as [number, string, number, number]);
          setEBayCategories(transformedEBayCategories);
        })
        .catch(error => alert("Fehler beim Laden der Ebay Kategorien " + error.toString()))
    };

    const handleEbayAddClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setEbayErrorMessage("");
      setEbayInfoMessage("");

      if (!ebayPlentyName) {
        setEbayErrorMessage("Es muss eine gültige Plenty Kategorien ID eingegeben werden.");
        return;
      } else if (!ebayPlentyID || !eBayID1) {
        setEbayErrorMessage("Alle Felder müssen ausgefüllt werden!");
        return;
      }

      const data = {
        plenty_cat_id: Number(ebayPlentyID),
        ebay_category_id_1: Number(eBayID1),
        ebay_category_id_2: eBayID2 ? Number(eBayID2) : null
      };

      await axios.post(`${backendUrl}/admin_panel/add_ebay_category/`, data, config)
        .then(() => {
          setEbayInfoMessage("Das Hinzufügen der Zuordnung war erfolgreich.");
          getEBayCategories();
          setEbayPlentyID('');
          setEbayPlentyName('');
          setEBayID1('');
          setEBayID2('');
        })
        .catch((err) => {
          handleError(err, setEbayErrorMessage);
        });
    };

    const handleEbayDeleteClick = async (event: React.MouseEvent<HTMLButtonElement>, data: any) => {
      event.preventDefault();
      setEbayErrorMessage("");

      await axios.delete(`${backendUrl}/admin_panel/remove_ebay_category/`, {...config, data: data})
        .then(() => {
          getEBayCategories();
          setEbayInfoMessage("Eintrag erfolgreich gelöscht.");
        })
        .catch((err) => {
          handleError(err, setEbayErrorMessage);
        });
    }

    // ==========================================
    // ============== HOOD LOGIC ================
    // ==========================================
    const getHoodCategories = async () => {
      await axios.get(`${backendUrl}/general/get_hood_categories/`, config)
        .then(response => {
          const responseDict = response.data;
          const transformedHoodCategories = Object.entries(responseDict).map(
            ([key, value]: [string, { plenty_cat_name: string; hood_category_id: number; }]) =>
              [Number(key), value.plenty_cat_name, value.hood_category_id] as [number, string, number]
          );
          setHoodCategories(transformedHoodCategories);
        })
        .catch(error => alert("Fehler beim Laden der Hood Kategorien " + error.toString()))
    };

    const handleHoodAddClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setHoodErrorMessage("");
      setHoodInfoMessage("");

      if (!hoodPlentyName) {
        setHoodErrorMessage("Es muss eine gültige Plenty Kategorien ID eingegeben werden.");
        return;
      } else if (!hoodPlentyID || !hoodID) {
        setHoodErrorMessage("Alle Felder müssen ausgefüllt werden!");
        return;
      }

      const data = {
        plenty_cat_id: Number(hoodPlentyID),
        hood_category_id: Number(hoodID)
      };

      await axios.post(`${backendUrl}/admin_panel/add_hood_category/`, data, config)
        .then(() => {
          setHoodInfoMessage("Das Hinzufügen der Zuordnung war erfolgreich.");
          getHoodCategories();
          setHoodPlentyID('');
          setHoodPlentyName('');
          setHoodID('');
        })
        .catch((err) => {
          handleError(err, setHoodErrorMessage);
        });
    };

    const handleHoodDeleteClick = async (event: React.MouseEvent<HTMLButtonElement>, data: any) => {
      event.preventDefault();
      setHoodErrorMessage("");

      await axios.delete(`${backendUrl}/admin_panel/remove_hood_category/`, {...config, data: data})
        .then(() => {
          getHoodCategories();
          setHoodInfoMessage("Eintrag erfolgreich gelöscht.");
        })
        .catch((err) => {
          handleError(err, setHoodErrorMessage);
        });
    }

    // Shared Error Handler
    const handleError = (err: any, setError: (msg: string) => void) => {
      const error_str = (!err.response)
        ? "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar."
        : (err.response.status === 500)
          ? "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan)."
          : err.response.data?.detail?.toString();
      setError(error_str);
    };

    return (
      <div style={{display: 'flex', flexDirection: 'column', gap: "30px", alignItems: 'center', width: "100%"}}>

        <div style={{
          width: "80%",
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: "10px",
          marginBottom: "20px"
        }}>
          <h3>Marktplatz-Abdeckung</h3>
          <p>Hier siehst du auf einen Blick, welche Kategorien bereits mit den Marktplätzen verknüpft sind.</p>
          <div style={{
            width: "100%",
            display: "flex",
            justifyContent: "flex-start",
            backgroundColor: "rgba(0,0,0,0.02)",
            padding: "15px",
            borderRadius: "8px"
          }}>
            <CategoryTreeAddRemove
              singleSelection={true}
              selectedItems={readOnlySelection}
              onSelectedItemsChange={() => {
              }} // Read-only tree
              ottoMap={currentOttoMap}
              ebayMap={currentEbayMap}
              hoodMap={currentHoodMap}
            />
          </div>
        </div>

        <hr style={{width: "80%", border: "0", borderTop: "2px solid #eee", margin: "10px 0"}}/>

        {/* ================================================================================== */}
        {/* ===================================== OTTO UI ==================================== */}
        {/* ================================================================================== */}
        <div style={{width: "100%", display: 'flex', flexDirection: 'column', alignItems: 'center', gap: "10px"}}>
          <h3>Otto Kategorien</h3>
          {renderScrollDownButton(otto_ref)}
          <div style={{display: "flex", flexDirection: "row", justifyContent: "center", width: "100%"}}>
            <table style={{width: "80%", borderCollapse: "collapse", marginTop: "10px"}}>
              <thead>
              <tr style={{borderBottom: "2px solid #ccc", textAlign: "center"}}>
                <th style={{padding: "10px", width: "15%"}}>Plenty ID</th>
                <th style={{padding: "10px", width: "30%"}}>Plenty Name</th>
                <th style={{padding: "10px", width: "15%"}}>Otto ID</th>
                <th style={{padding: "10px", width: "30%"}}>Otto Name</th>
                <th style={{padding: "10px"}}></th>
              </tr>
              </thead>
              <tbody>
              {ottoCategories && ottoCategories.length > 0 && ottoCategories.map(([plenty_id, plenty_name, otto_id, otto_name], idx) => (
                <tr key={idx} style={{borderBottom: "1px solid #eee"}}>
                  <td style={{padding: "10px"}}>{plenty_id}</td>
                  <td style={{padding: "10px"}}>{plenty_name}</td>
                  <td style={{padding: "10px"}}>{otto_id}</td>
                  <td style={{padding: "10px"}}>{otto_name}</td>
                  <td style={{padding: "10px"}}>
                    <DefaultButton
                      label="-"
                      color={"red"}
                      variant={"contained"}
                      size="small"
                      sx={{minWidth: "30px", width: "30px", height: "30px"}}
                      onClick={async () => {
                        await axios.delete(`${backendUrl}/admin_panel/remove_otto_category/${plenty_id}/${otto_id}/`, config)
                          .then(() => {
                            getOttoCategories();
                            setOttoInfoMessage("Eintrag erfolgreich gelöscht.");
                          })
                          .catch((err) => handleError(err, setOttoErrorMessage));
                      }}
                    />
                  </td>
                </tr>
              ))}
              </tbody>
            </table>
          </div>
          <div style={{display: 'flex', flexDirection: 'row', gap: "15px", width: "80%", justifyContent: "center"}}>
            <CustomTextField label={"Plenty Kategorie ID"} value={ottoPlentyID}
                             onChange={(e) => setOttoPlentyID(e.target.value)} inputMode={"integer"} is_required={true}
                             sx={{width: "25%"}} onBlur={() => {
              const catObj = categories.get(Number(ottoPlentyID));
              if (!catObj) {
                setOttoErrorMessage("Die eingegebene Plenty Kategorie ID ist nicht vorhanden!")
                setOttoPlentyName("");
              } else {
                setOttoPlentyName(catObj.name);
              }
            }}/>
            <CustomTextField label={"Plenty Kategorie Name"} value={ottoPlentyName} inputMode={"text"}
                             is_required={true} is_disabled={true} sx={{width: "40%"}}/>
            <CustomTextField label={"Otto Kategorie ID"} value={ottoID} onChange={(e) => setOttoID(e.target.value)}
                             inputMode={"integer"} is_required={true} sx={{width: "25%"}}/>
            <CustomTextField label={"Otto Kategorie Name"} value={ottoName}
                             onChange={(e) => setOttoName(e.target.value)} inputMode={"text"} is_required={true}
                             sx={{width: "40%"}} ref={otto_ref}/>
          </div>
          <SubmitButton label="Otto Kategorie hinzufügen" size="small" onClick={handleOttoAddClick}
                        sx={{width: "50%"}}/>
          {renderErrorMessage(ottoErrorMessage)}
          {renderInfoMessage(ottoInfoMessage)}
        </div>

        <hr style={{width: "80%", border: "0", borderTop: "2px solid #eee", margin: "10px 0"}}/>

        {/* ================================================================================== */}
        {/* ===================================== EBAY UI ==================================== */}
        {/* ================================================================================== */}
        <div style={{width: "100%", display: 'flex', flexDirection: 'column', alignItems: 'center', gap: "10px"}}>
          <h3>eBay Kategorien</h3>
          {renderScrollDownButton(ebay_ref)}
          <div style={{display: "flex", flexDirection: "row", justifyContent: "center", width: "100%"}}>
            <table style={{width: "80%", borderCollapse: "collapse", marginTop: "10px"}}>
              <thead>
              <tr style={{borderBottom: "2px solid #ccc", textAlign: "center"}}>
                <th style={{padding: "10px", width: "20%"}}>Plenty ID</th>
                <th style={{padding: "10px", width: "25%"}}>Plenty Name</th>
                <th style={{padding: "10px", width: "30%"}}>EBay Kategorie 1</th>
                <th style={{padding: "10px", width: "30%"}}>EBay Kategorie 2</th>
                <th style={{padding: "10px"}}></th>
              </tr>
              </thead>
              <tbody>
              {eBayCategories && eBayCategories.length > 0 && eBayCategories.map(([plenty_id, plenty_name, ebay_id_1, ebay_id_2], idx) => (
                <tr key={idx} style={{borderBottom: "1px solid #eee"}}>
                  <td style={{padding: "10px"}}>{plenty_id}</td>
                  <td style={{padding: "10px"}}>{plenty_name}</td>
                  <td style={{padding: "10px"}}>{ebay_id_1}</td>
                  <td style={{padding: "10px"}}>{ebay_id_2}</td>
                  <td style={{padding: "10px"}}>
                    <DefaultButton
                      label="-"
                      color={"red"}
                      variant={"contained"}
                      size="small"
                      sx={{minWidth: "30px", width: "30px", height: "30px"}}
                      onClick={async (e) => {
                        await handleEbayDeleteClick(e, {
                          plenty_cat_id: plenty_id,
                          ebay_category_id_1: ebay_id_1,
                          ebay_category_id_2: ebay_id_2
                        })
                      }}
                    />
                  </td>
                </tr>
              ))}
              </tbody>
            </table>
          </div>
          <div style={{display: 'flex', flexDirection: 'row', gap: "15px", width: "80%", justifyContent: "center"}}>
            <CustomTextField label={"Plenty Kategorie ID"} value={ebayPlentyID}
                             onChange={(e) => setEbayPlentyID(e.target.value)} inputMode={"integer"} is_required={true}
                             sx={{width: "25%"}} onBlur={() => {
              const catObj = categories.get(Number(ebayPlentyID));
              if (!catObj) {
                setEbayErrorMessage("Die eingegebene Plenty Kategorie ID ist nicht vorhanden!")
                setEbayPlentyName("");
              } else {
                setEbayPlentyName(catObj.name);
              }
            }}/>
            <CustomTextField label={"Plenty Kategorie Name"} value={ebayPlentyName} inputMode={"text"}
                             is_required={true} is_disabled={true} sx={{width: "40%"}}/>
            <CustomTextField label={"EBay Kategorie 1"} value={eBayID1} onChange={(e) => setEBayID1(e.target.value)}
                             inputMode={"integer"} is_required={true} sx={{width: "25%"}}/>
            <CustomTextField label={"EBay Kategorie 2"} value={eBayID2} onChange={(e) => setEBayID2(e.target.value)}
                             inputMode={"integer"} is_required={false} sx={{width: "25%"}} ref={ebay_ref}/>
          </div>
          <SubmitButton label="EBay Kategorie hinzufügen" size="small" onClick={handleEbayAddClick}
                        sx={{width: "50%"}}/>
          {renderErrorMessage(ebayErrorMessage)}
          {renderInfoMessage(ebayInfoMessage)}
        </div>

        <hr style={{width: "80%", border: "0", borderTop: "2px solid #eee", margin: "10px 0"}}/>

        {/* ================================================================================== */}
        {/* ===================================== HOOD UI ==================================== */}
        {/* ================================================================================== */}
        <div style={{width: "100%", display: 'flex', flexDirection: 'column', alignItems: 'center', gap: "10px"}}>
          <h3>Hood Kategorien</h3>
          {renderScrollDownButton(hood_ref)}
          <div style={{display: "flex", flexDirection: "row", justifyContent: "center", width: "100%"}}>
            <table style={{width: "80%", borderCollapse: "collapse", marginTop: "10px"}}>
              <thead>
              <tr style={{borderBottom: "2px solid #ccc", textAlign: "center"}}>
                <th style={{padding: "10px", width: "20%"}}>Plenty ID</th>
                <th style={{padding: "10px", width: "25%"}}>Plenty Name</th>
                <th style={{padding: "10px", width: "30%"}}>Hood ID</th>
                <th style={{padding: "10px"}}></th>
              </tr>
              </thead>
              <tbody>
              {hoodCategories && hoodCategories.length > 0 && hoodCategories.map(([plenty_id, plenty_name, hood_id], idx) => (
                <tr key={idx} style={{borderBottom: "1px solid #eee"}}>
                  <td style={{padding: "10px"}}>{plenty_id}</td>
                  <td style={{padding: "10px"}}>{plenty_name}</td>
                  <td style={{padding: "10px"}}>{hood_id}</td>
                  <td style={{padding: "10px"}}>
                    <DefaultButton
                      label="-"
                      color={"red"}
                      variant={"contained"}
                      size="small"
                      sx={{minWidth: "30px", width: "30px", height: "30px"}}
                      onClick={async (e) => {
                        await handleHoodDeleteClick(e, {plenty_cat_id: plenty_id, hood_category_id: hood_id})
                      }}
                    />
                  </td>
                </tr>
              ))}
              </tbody>
            </table>
          </div>
          <div style={{display: 'flex', flexDirection: 'row', gap: "15px", width: "80%", justifyContent: "center"}}>
            <CustomTextField label={"Plenty Kategorie ID"} value={hoodPlentyID}
                             onChange={(e) => setHoodPlentyID(e.target.value)} inputMode={"integer"} is_required={true}
                             sx={{width: "25%"}} onBlur={() => {
              const catObj = categories.get(Number(hoodPlentyID));
              if (!catObj) {
                setHoodErrorMessage("Die eingegebene Plenty Kategorie ID ist nicht vorhanden!")
                setHoodPlentyName("");
              } else {
                setHoodPlentyName(catObj.name);
              }
            }}/>
            <CustomTextField label={"Plenty Kategorie Name"} value={hoodPlentyName} inputMode={"text"}
                             is_required={true} is_disabled={true} sx={{width: "40%"}}/>
            <CustomTextField label={"Hood ID"} value={hoodID} onChange={(e) => setHoodID(e.target.value)}
                             inputMode={"integer"} is_required={true} sx={{width: "25%"}} ref={hood_ref}/>

          </div>
          <SubmitButton label="Hood Kategorie hinzufügen" size="small" onClick={handleHoodAddClick}
                        sx={{width: "50%"}}/>
          {renderErrorMessage(hoodErrorMessage)}
          {renderInfoMessage(hoodInfoMessage)}
        </div>
      </div>
    );
  };
  // Tab 3 Combined Tab (Color, WoodType, Material)
  const RenderMaterialManagementPanel = () => {

    // ==========================================
    // ============== COLOR STATE ===============
    // ==========================================
    const [colorErrorMessage, setColorErrorMessage] = useState<string | null>(null);
    const [colorInfoMessage, setColorInfoMessage] = useState<string | null>(null);
    const [colorID, setColorID] = useState<string>("");
    const [colorName, setColorName] = useState<string>("");

    // ==========================================
    // ============== WOOD STATE ================
    // ==========================================
    const [woodErrorMessage, setWoodErrorMessage] = useState<string | null>(null);
    const [woodInfoMessage, setWoodInfoMessage] = useState<string | null>(null);
    const [woodTypeID, setWoodTypeID] = useState<string>("");
    const [woodTypeName, setWoodTypeName] = useState<string>("");

    // ==========================================
    // ============= MATERIAL STATE =============
    // ==========================================
    const [materialErrorMessage, setMaterialErrorMessage] = useState<string | null>(null);
    const [materialInfoMessage, setMaterialInfoMessage] = useState<string | null>(null);
    const [materialID, setMaterialID] = useState<string>("");
    const [materialName, setMaterialName] = useState<string>("");

    // Shared Error Handler
    const handleError = (err: any, setError: (msg: string) => void) => {
      const error_str = (!err.response)
        ? "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar."
        : (err.response.status === 500)
          ? "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan)."
          : err.response.data?.detail?.toString();
      setError(error_str);
    };

    // ==========================================
    // ============== COLOR LOGIC ===============
    // ==========================================
    const getColors = async () => {
      await axios.get(`${backendUrl}/admin_panel/get_colors/`, config)
        .then(response => setColors(response.data))
        .catch(error => alert("Fehler beim Laden der Farben " + error.toString()));
    };

    const handleAddColorClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setColorErrorMessage("");
      setColorInfoMessage("");

      if (!colorID || !colorName) {
        setColorErrorMessage("Beide Felder müssen ausgefüllt sein!");
        return;
      }

      const data = {id: Number(colorID), name: colorName};
      await axios.post(`${backendUrl}/admin_panel/add_color/`, data, config)
        .then(() => {
          setColorInfoMessage("Das Hinzufügen der Zuordnung war erfolgreich.");
          getColors();
          setColorID("");
          setColorName("");
        })
        .catch((err) => handleError(err, setColorErrorMessage));
    };

    const handleDeleteColorClick = async (event: React.MouseEvent<HTMLButtonElement>, data: any) => {
      event.preventDefault();
      setColorErrorMessage("");
      await axios.delete(`${backendUrl}/admin_panel/remove_color/`, {...config, data: data})
        .then(() => {
          getColors();
          setColorInfoMessage("Eintrag erfolgreich gelöscht.");
        })
        .catch((err) => handleError(err, setColorErrorMessage));
    }

    // ==========================================
    // ============== WOOD LOGIC ================
    // ==========================================
    const getWoodTypes = async () => {
      await axios.get(`${backendUrl}/admin_panel/get_wood_types/`, config)
        .then(response => setWoodTypes(response.data))
        .catch(error => alert("Fehler beim Laden der Holzarten " + error.toString()));
    };

    const handleAddWoodClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setWoodErrorMessage("");
      setWoodInfoMessage("");

      if (!woodTypeID || !woodTypeName) {
        setWoodErrorMessage("Beide Felder müssen ausgefüllt sein!");
        return;
      }

      const data = {id: Number(woodTypeID), name: woodTypeName};
      await axios.post(`${backendUrl}/admin_panel/add_wood_type/`, data, config)
        .then(() => {
          setWoodInfoMessage("Das Hinzufügen der Zuordnung war erfolgreich.");
          getWoodTypes();
          setWoodTypeID("");
          setWoodTypeName("");
        })
        .catch((err) => handleError(err, setWoodErrorMessage));
    };

    const handleDeleteWoodClick = async (event: React.MouseEvent<HTMLButtonElement>, data: any) => {
      event.preventDefault();
      setWoodErrorMessage("");
      await axios.delete(`${backendUrl}/admin_panel/remove_wood_type/`, {...config, data: data})
        .then(() => {
          getWoodTypes();
          setWoodInfoMessage("Eintrag erfolgreich gelöscht.");
        })
        .catch((err) => handleError(err, setWoodErrorMessage));
    }

    // ==========================================
    // ============ MATERIAL LOGIC ==============
    // ==========================================
    const getMaterials = async () => {
      await axios.get(`${backendUrl}/admin_panel/get_materials/`, config)
        .then(response => setMaterials(response.data))
        .catch(error => alert("Fehler beim Laden der Materialien " + error.toString()));
    };

    const handleAddMaterialClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setMaterialErrorMessage("");
      setMaterialInfoMessage("");

      if (!materialID || !materialName) {
        setMaterialErrorMessage("Beide Felder müssen ausgefüllt sein!");
        return;
      }

      const data = {id: Number(materialID), name: materialName};
      await axios.post(`${backendUrl}/admin_panel/add_material/`, data, config)
        .then(() => {
          setMaterialInfoMessage("Das Hinzufügen der Zuordnung war erfolgreich.");
          getMaterials();
          setMaterialID("");
          setMaterialName("");
        })
        .catch((err) => handleError(err, setMaterialErrorMessage));
    };

    const handleDeleteMaterialClick = async (event: React.MouseEvent<HTMLButtonElement>, data: any) => {
      event.preventDefault();
      setMaterialErrorMessage("");
      await axios.delete(`${backendUrl}/admin_panel/remove_material/`, {...config, data: data})
        .then(() => {
          getMaterials();
          setMaterialInfoMessage("Eintrag erfolgreich gelöscht.");
        })
        .catch((err) => handleError(err, setMaterialErrorMessage));
    }

    return (
      <div style={{display: 'flex', flexDirection: 'column', gap: "30px", alignItems: 'center', width: "100%"}}>

        {/* ================================================================================== */}
        {/* ===================================== COLORS ===================================== */}
        {/* ================================================================================== */}
        <div style={{width: "100%", display: 'flex', flexDirection: 'column', alignItems: 'center', gap: "10px"}}>
          <h3>Farben verwalten</h3>
          {renderScrollDownButton(color_ref)}
          <div style={{display: "flex", flexDirection: "row", justifyContent: "center", width: "30%"}}>
            <table style={{width: "100%", borderCollapse: "collapse", marginTop: "10px"}}>
              <thead>
              <tr style={{borderBottom: "2px solid #ccc", textAlign: "center", width: "80%"}}>
                <th style={{padding: "10px", width: "20%"}}>ID</th>
                <th style={{padding: "10px", minWidth: "50%"}}>Name</th>
                <th style={{padding: "10px"}}></th>
              </tr>
              </thead>
              <tbody>
              {colors && colors.length > 0 && colors.map(([id, name], idx) => (
                <tr key={idx} style={{borderBottom: "1px solid #eee"}}>
                  <td style={{padding: "10px"}}>{id}</td>
                  <td style={{padding: "10px"}}>{name}</td>
                  <td style={{padding: "10px"}}>
                    <DefaultButton
                      label="-"
                      color={"red"}
                      variant={"contained"}
                      size="small"
                      sx={{minWidth: "30px", width: "30px", height: "30px"}}
                      onClick={async (e) => {
                        await handleDeleteColorClick(e, {id: id})
                      }}
                    />
                  </td>
                </tr>
              ))}
              </tbody>
            </table>
          </div>
          <div style={{display: 'flex', flexDirection: 'row', gap: "15px", justifyContent: "center"}}>
            <CustomTextField label={"Farb ID"} value={colorID} onChange={(e) => setColorID(e.target.value)}
                             inputMode={"integer"} is_required={true} sx={{width: "25%"}}/>
            <CustomTextField label={"Farb Name"} value={colorName} onChange={(e) => setColorName(e.target.value)}
                             inputMode={"text"} is_required={true} sx={{width: "40%"}} ref={color_ref}/>
          </div>
          <SubmitButton label="Farbe hinzufügen" size="small" onClick={handleAddColorClick} sx={{width: "50%"}}/>
          {renderErrorMessage(colorErrorMessage)}
          {renderInfoMessage(colorInfoMessage)}
        </div>

        <hr style={{width: "80%", border: "0", borderTop: "2px solid #eee", margin: "10px 0"}}/>

        {/* ================================================================================== */}
        {/* =================================== WOOD TYPES =================================== */}
        {/* ================================================================================== */}
        <div style={{width: "100%", display: 'flex', flexDirection: 'column', alignItems: 'center', gap: "10px"}}>
          <h3>Holzarten verwalten</h3>
          {renderScrollDownButton(woodType_ref)}
          <div style={{display: "flex", flexDirection: "row", justifyContent: "center", width: "30%"}}>
            <table style={{width: "100%", borderCollapse: "collapse", marginTop: "10px"}}>
              <thead>
              <tr style={{borderBottom: "2px solid #ccc", textAlign: "center", width: "80%"}}>
                <th style={{padding: "10px", width: "20%"}}>ID</th>
                <th style={{padding: "10px", minWidth: "50%"}}>Name</th>
                <th style={{padding: "10px"}}></th>
              </tr>
              </thead>
              <tbody>
              {woodTypes && woodTypes.length > 0 && woodTypes.map(([id, name], idx) => (
                <tr key={idx} style={{borderBottom: "1px solid #eee"}}>
                  <td style={{padding: "10px"}}>{id}</td>
                  <td style={{padding: "10px"}}>{name}</td>
                  <td style={{padding: "10px"}}>
                    <DefaultButton
                      label="-"
                      color={"red"}
                      variant={"contained"}
                      size="small"
                      sx={{minWidth: "30px", width: "30px", height: "30px"}}
                      onClick={async (e) => {
                        await handleDeleteWoodClick(e, {id: id})
                      }}
                    />
                  </td>
                </tr>
              ))}
              </tbody>
            </table>
          </div>
          <div style={{display: 'flex', flexDirection: 'row', gap: "15px", justifyContent: "center"}}>
            <CustomTextField label={"Holzart ID"} value={woodTypeID} onChange={(e) => setWoodTypeID(e.target.value)}
                             inputMode={"integer"} is_required={true} sx={{width: "25%"}}/>
            <CustomTextField label={"Holzart Name"} value={woodTypeName}
                             onChange={(e) => setWoodTypeName(e.target.value)} inputMode={"text"} is_required={true}
                             sx={{width: "40%"}} ref={woodType_ref}/>
          </div>
          <SubmitButton label="Holzart hinzufügen" size="small" onClick={handleAddWoodClick} sx={{width: "50%"}}/>
          {renderErrorMessage(woodErrorMessage)}
          {renderInfoMessage(woodInfoMessage)}
        </div>

        <hr style={{width: "80%", border: "0", borderTop: "2px solid #eee", margin: "10px 0"}}/>

        {/* ================================================================================== */}
        {/* =================================== MATERIALS ==================================== */}
        {/* ================================================================================== */}
        <div style={{width: "100%", display: 'flex', flexDirection: 'column', alignItems: 'center', gap: "10px"}}>
          <h3>Materialien verwalten</h3>
          {renderScrollDownButton(material_ref)}
          <div style={{display: "flex", flexDirection: "row", justifyContent: "center", width: "30%"}}>
            <table style={{width: "100%", borderCollapse: "collapse", marginTop: "10px"}}>
              <thead>
              <tr style={{borderBottom: "2px solid #ccc", textAlign: "center", width: "80%"}}>
                <th style={{padding: "10px", width: "20%"}}>ID</th>
                <th style={{padding: "10px", minWidth: "50%"}}>Name</th>
                <th style={{padding: "10px"}}></th>
              </tr>
              </thead>
              <tbody>
              {materials && materials.length > 0 && materials.map(([id, name], idx) => (
                <tr key={idx} style={{borderBottom: "1px solid #eee"}}>
                  <td style={{padding: "10px"}}>{id}</td>
                  <td style={{padding: "10px"}}>{name}</td>
                  <td style={{padding: "10px"}}>
                    <DefaultButton
                      label="-"
                      color={"red"}
                      variant={"contained"}
                      size="small"
                      sx={{minWidth: "30px", width: "30px", height: "30px"}}
                      onClick={async (e) => {
                        await handleDeleteMaterialClick(e, {id: id})
                      }}
                    />
                  </td>
                </tr>
              ))}
              </tbody>
            </table>
          </div>
          <div style={{display: 'flex', flexDirection: 'row', gap: "15px", justifyContent: "center"}}>
            <CustomTextField label={"Material ID"} value={materialID} onChange={(e) => setMaterialID(e.target.value)}
                             inputMode={"integer"} is_required={true} sx={{width: "25%"}}/>
            <CustomTextField label={"Material Name"} value={materialName}
                             onChange={(e) => setMaterialName(e.target.value)} inputMode={"text"} is_required={true}
                             sx={{width: "40%"}} ref={material_ref}/>
          </div>
          <SubmitButton label="Material hinzufügen" size="small" onClick={handleAddMaterialClick} sx={{width: "50%"}}/>
          {renderErrorMessage(materialErrorMessage)}
          {renderInfoMessage(materialInfoMessage)}
        </div>

      </div>
    );
  };
  // Tab 4
  const RenderInvalidWordPanel = () => {
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [infoMessage, setInfoMessage] = useState<string | null>(null);
    const [invalidWord, setInvalidWord] = useState<string>("");
    const getInvalidWords = async () => {
      await axios.get(`${backendUrl}/admin_panel/get_invalid_words/`, config)
        .then(response => {
          setInvalidWords(response.data);
        })
        .catch(error => alert("Fehler beim Laden der verbotenen Worte " + error.toString()))
    };

    const handleAddClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setErrorMessage("");
      setInfoMessage("");

      if (!invalidWord) {
        setErrorMessage("Das Feld muss ausgefüllt sein!");
        return;
      }

      const data = {
        invalid_word: invalidWord
      };
      await axios
        .post(
          `${backendUrl}/admin_panel/add_invalid_word/`,
          data,
          config
        )
        .then(() => {
          setInfoMessage("Das Hinzufügen der Zuordnung war erfolgreich.");
          getInvalidWords();
          setInvalidWord('')
        })
        .catch((err) => {
          const error_str = (!err.response) ? "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar." // This will handle network errors or cases where the server doesn't respond at all
            : (err.response.status === 500) ? "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan)."
              : err.response.data?.detail?.toString();
          setErrorMessage(error_str);
        });
    };

    const handleDeleteClick = async (event: React.MouseEvent<HTMLButtonElement>, data) => {
      event.preventDefault();
      setErrorMessage("");

      await axios
          .delete(`${backendUrl}/admin_panel/remove_invalid_word/`, {
            ...config,
            data: data
          })
          .then(() => {
            getInvalidWords();
            setInfoMessage("Eintrag erfolgreich gelöscht.");
          })
          .catch((err) => {
            const error_str = (!err.response)
                ? "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar."
                : (err.response.status === 500)
                    ? "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan)."
                    : err.response.data?.detail?.toString();
            setErrorMessage(error_str);
          });
    }

    return(
        <div style={{display: 'flex', flexDirection: 'column', gap: "15px", alignItems: 'center'}}>
          {renderScrollDownButton(invalid_words_ref)}
          <div style={{display: "flex", flexDirection: "row", justifyContent: "center"}}>
            <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "10px" }}>
              <thead>
              <tr style={{ borderBottom: "2px solid #ccc", textAlign: "center" }}>
                <th style={{ padding: "10px", width: "100%" }}>Verbotenes Wort</th>
                <th style={{ padding: "10px" }}> </th>
              </tr>
              </thead>
              <tbody>
              {invalidWords && invalidWords.length > 0 && invalidWords.map((word,idx) => (
                  <tr key={idx} style={{ borderBottom: "1px solid #eee" }}>
                    <td style={{ padding: "10px" }}>{word}</td>
                    <td style={{ padding: "10px" }}>
                      <DefaultButton
                          label="-"
                          color={"red"}
                          variant={"contained"}
                          size="small"
                          sx={{ minWidth: "30px", width: "30px", height: "30px" }}
                          onClick={async (e) => {await handleDeleteClick(e, {invalid_word: word})}}
                      />
                    </td>
                  </tr>
              ))}
              </tbody>
            </table>
          </div>
          <div style={{display: 'flex', flexDirection: 'row', gap: "15px"}}>
            <CustomTextField label={"Verbotenes Wort"} value={invalidWord} onChange={(e) => setInvalidWord(e.target.value)} inputMode={"string"} is_required={true} sx={{width: "100%"}} ref={invalid_words_ref}/>


          </div>
          <SubmitButton label="Verbotenes Wort hinzufügen" size="small" onClick={handleAddClick} sx={{width: "30%"}}/>
          {renderErrorMessage(errorMessage)}
          {renderInfoMessage(infoMessage)}
        </div>

    )

  };
  // Tab 5
  const RenderManufacturerSupplierPanel = () => {
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [infoMessage, setInfoMessage] = useState<string | null>(null);
    const [manufacturerID, setManufacturerID] = useState<string>("");
    const [manufacturerName, setManufacturerName] = useState<string>("");
    const [supplierID, setSupplierID] = useState<string>("");
    const [supplierName, setSupplierName] = useState<string>("");
    const getManufacturerSupplierConnections = async () => {
      await axios.get(`${backendUrl}/admin_panel/get_manufacturer_suppliers/`, config)
        .then(response => {
          setManufacturerSuppliers(response.data);
        })
        .catch(error => alert("Fehler beim Laden der verbotenen Worte " + error.toString()))
    };

    const handleAddClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setErrorMessage("");
      setInfoMessage("");

      if (!manufacturerID || !manufacturerName || !supplierID || !supplierName) {
        setErrorMessage("Alle Felder müssen ausgefüllt sein!");
        return;
      }

      const data = {
        manufacturer_id: Number(manufacturerID),
        manufacturer_name: manufacturerName,
        supplier_id: Number(supplierID),
        supplier_name: supplierName
      };
      await axios
        .post(
          `${backendUrl}/admin_panel/add_manufacturer_supplier/`,
          data,
          config
        )
        .then(() => {
          setInfoMessage("Das Hinzufügen der Zuordnung war erfolgreich.");
          getManufacturerSupplierConnections();
          setManufacturerID("");
          setManufacturerName("");
          setSupplierID("");
          setSupplierName("");
        })
        .catch((err) => {
          const error_str = (!err.response) ? "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar." // This will handle network errors or cases where the server doesn't respond at all
            : (err.response.status === 500) ? "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan)."
              : err.response.data?.detail?.toString();
          setErrorMessage(error_str);
        });
    };

    const handleDeleteClick = async (event: React.MouseEvent<HTMLButtonElement>, data) => {
      event.preventDefault();
      setErrorMessage("");

      await axios
        .delete(`${backendUrl}/admin_panel/remove_manufacturer_supplier/`, {
          ...config,
          data: data
        })
        .then(() => {
          getManufacturerSupplierConnections();
          setInfoMessage("Eintrag erfolgreich gelöscht.");
        })
        .catch((err) => {
          const error_str = (!err.response)
            ? "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar."
            : (err.response.status === 500)
              ? "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan)."
              : err.response.data?.detail?.toString();
          setErrorMessage(error_str);
        });
    }

    return <div style={{display: 'flex', flexDirection: 'column', gap: "15px", alignItems: 'center'}}>
      {renderScrollDownButton(manufacturerSupplier_ref)}
      <div style={{display: "flex", flexDirection: "row", justifyContent: "center"}}>
        <table style={{width: "75%", borderCollapse: "collapse", marginTop: "10px"}}>
          <thead>
          <tr style={{borderBottom: "2px solid #ccc", textAlign: "center"}}>
            <th style={{padding: "10px", width: "12%"}}>Hersteller ID</th>
            <th style={{padding: "10px", width: "29%"}}>Hersteller Name</th>
            <th style={{padding: "10px", width: "12%"}}>Lieferant ID</th>
            <th style={{padding: "10px", width: "29%"}}>Lieferant Name</th>
            <th style={{padding: "10px", width: "5%"}}></th>
          </tr>
          </thead>
          <tbody>
          {manufacturerSuppliers && manufacturerSuppliers.length > 0 && manufacturerSuppliers.map(([manufacturerEntryID, manufacturerEntryName, supplierEntryID, supplierEntryName], idx) => (
            <tr key={idx} style={{borderBottom: "1px solid #eee"}}>
              <td style={{padding: "10px"}}>{manufacturerEntryID}</td>
              <td style={{padding: "10px"}}>{manufacturerEntryName}</td>
              <td style={{padding: "10px"}}>{supplierEntryID}</td>
              <td style={{padding: "10px"}}>{supplierEntryName}</td>
              <td style={{padding: "10px"}}>
                <DefaultButton
                  label="-"
                  color={"red"}
                  variant={"contained"}
                  size="small"
                  sx={{minWidth: "30px", width: "30px", height: "30px"}}
                  onClick={async (e) => {
                    await handleDeleteClick(e, {manufacturer_id: manufacturerEntryID, supplier_id: supplierEntryID})
                  }}
                />
              </td>
            </tr>
          ))}
          </tbody>
        </table>
      </div>
      <div style={{display: 'flex', flexDirection: 'row', gap: "15px", justifyContent: "center"}}>
        <CustomTextField label={"Hersteller ID"} value={manufacturerID}
                         onChange={(e) => setManufacturerID(e.target.value)} inputMode={"integer"} is_required={true}
                         sx={{width: "15%"}}/>
        <CustomTextField label={"Hersteller Name"} value={manufacturerName}
                         onChange={(e) => setManufacturerName(e.target.value)} inputMode={"text"} is_required={true}
                         sx={{width: "30%"}}/>
        <CustomTextField label={"Lieferant ID"} value={supplierID} onChange={(e) => setSupplierID(e.target.value)}
                         inputMode={"integer"} is_required={true} sx={{width: "15%"}}/>
        <CustomTextField label={"Lieferant Name"} value={supplierName} onChange={(e) => setSupplierName(e.target.value)}
                         inputMode={"text"} is_required={false} sx={{width: "30%"}} ref={manufacturerSupplier_ref}/>
      </div>
      <SubmitButton label="Verknüpfung hinzufügen" size="small" onClick={handleAddClick} sx={{width: "50%"}}/>
      {renderErrorMessage(errorMessage)}
      {renderInfoMessage(infoMessage)}
    </div>
  };
  // Tab 6
  const RenderManufacturerAvailabilityConstraintPanel = () => {
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [infoMessage, setInfoMessage] = useState<string | null>(null);
    const [selectedManufacturer, setSelectedManufacturer] = useState<{ value: string | number; label: string }>(null);
    const [selectedMarketsAvailabilities, setSelectedMarketsAvailabilities] = useState<{
      value: string | number;
      label: string
    }[]>([]);
    const getManufacturerAvailabilityConstraints = async () => {
      await axios.get(`${backendUrl}/admin_panel/get_availability_manufacturer_constraints/`, config)
        .then(response => {
          setManufacturerMarketsAvailabilityConstraints(response.data);
        })
        .catch(error => alert("Fehler beim Laden der verbotenen Worte " + error.toString()))
    };

    const handleAddClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setErrorMessage("");
      setInfoMessage("");

      if (!selectedManufacturer || !selectedMarketsAvailabilities) {
        setErrorMessage("Alle Felder müssen ausgefüllt sein!");
        return;
      } else if (selectedMarketsAvailabilities.length == 0) {
        setErrorMessage("Es muss mindestens eine Marktverfügbarkeit ausgewählt sein!");
        return;
      } else if (selectedMarketsAvailabilities.length == marketsAvailabilities.length) {
        setErrorMessage("Es dürfen nicht alle Marktverfügbarkeiten ausgewählt sein!");
        return;
      }

      const data = {
        manufacturer_id: Number(selectedManufacturer.value),
        market_availabilities: selectedMarketsAvailabilities.map(market => market.value)
      };
      await axios
        .post(
          `${backendUrl}/admin_panel/add_manufacturer_availabilitiy_constraint/`,
          data,
          config
        )
        .then(() => {
          setInfoMessage("Das Hinzufügen der Zuordnung war erfolgreich.");
          getManufacturerAvailabilityConstraints();
          setSelectedManufacturer(null);
          setSelectedMarketsAvailabilities([]);
        })
        .catch((err) => {
          const error_str = (!err.response) ? "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar." // This will handle network errors or cases where the server doesn't respond at all
            : (err.response.status === 500) ? "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan)."
              : err.response.data?.detail?.toString();
          setErrorMessage(error_str);
        });
    };

    const handleDeleteClick = async (event: React.MouseEvent<HTMLButtonElement>, data) => {
      event.preventDefault();
      setErrorMessage("");

      await axios
        .delete(`${backendUrl}/admin_panel/remove_manufacturer_availability_constraint/`, {
          ...config,
          data: data
        })
        .then(() => {
          getManufacturerAvailabilityConstraints();
          setInfoMessage("Eintrag erfolgreich gelöscht.");
        })
        .catch((err) => {
          const error_str = (!err.response)
            ? "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar."
            : (err.response.status === 500)
              ? "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan)."
              : err.response.data?.detail?.toString();
          setErrorMessage(error_str);
        });
    }

    return <div style={{display: 'flex', flexDirection: 'column', gap: "15px", alignItems: 'center'}}>
      {renderScrollDownButton(manufacturerAvailabilityConstraint_ref)}
      <div style={{display: "flex", flexDirection: "row", justifyContent: "center"}}>
        <table style={{width: "100%", borderCollapse: "collapse", marginTop: "10px"}}>
          <thead>
          <tr style={{borderBottom: "2px solid #ccc", textAlign: "center"}}>
            <th style={{padding: "10px", width: "18%"}}>Hersteller ID</th>
            <th style={{padding: "10px", width: "30%"}}>Hersteller Name</th>
            <th style={{padding: "10px", width: "50%"}}>Marktverfügbarkeit</th>
            <th style={{padding: "10px", width: "5%"}}></th>
          </tr>
          </thead>
          <tbody>
          {manufacturerMarketsAvailabilityConstraints && Object.entries(manufacturerMarketsAvailabilityConstraints).length > 0 && Object.entries(manufacturerMarketsAvailabilityConstraints).map(([_manID, _availabilities], idx) => (
            <tr key={idx} style={{borderBottom: "1px solid #eee"}}>
              <td style={{padding: "10px"}}>{_manID}</td>
              <td style={{padding: "10px"}}>{manufacturersNameFromId[_manID]}</td>
              <td style={{padding: "10px"}}>
                <SearchableMultiSelect items={marketsAvailabilities} inputLabel="Märkte" selectAllOption={true}
                                       selectAllInitially={false} disabled={true}
                                       value={_availabilities} setValue={() => {
                }}/>
              </td>
              <td style={{padding: "10px"}}>
                <DefaultButton
                  label="-"
                  color={"red"}
                  variant={"contained"}
                  size="small"
                  sx={{minWidth: "30px", width: "30px", height: "30px"}}
                  onClick={async (e) => {
                    await handleDeleteClick(e, {manufacturer_id: _manID})
                  }}
                />
              </td>
            </tr>
          ))}
          </tbody>
        </table>
      </div>
      <div style={{display: 'flex', flexDirection: 'row', gap: "15px", justifyContent: "center", width: "75%"}}>
        <SearchableSelect inputLabel={"Hersteller"} items={manufacturers} value={selectedManufacturer}
                          setValue={(newManufacturer) => setSelectedManufacturer(newManufacturer)} sx={{width: "50%"}}/>
        <SearchableMultiSelect items={marketsAvailabilities} inputLabel="Märkte" selectAllOption={true}
                               selectAllInitially={false}
                               value={selectedMarketsAvailabilities}
                               setValue={(newVal) => setSelectedMarketsAvailabilities(newVal)}/>
      </div>
      <div style={{display: 'flex', flexDirection: 'column', gap: "15px", alignItems: 'center', width: '100%'}}
           ref={manufacturerAvailabilityConstraint_ref}>
        <SubmitButton label="Verknüpfung hinzufügen" size="small" onClick={handleAddClick} sx={{width: "50%"}}/>
        {renderErrorMessage(errorMessage)}
        {renderInfoMessage(infoMessage)}
      </div>
    </div>
  };
  // Tab 7
  const RenderListingCreatorFieldPanel = () => {
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [infoMessage, setInfoMessage] = useState<string | null>(null);
    const fieldOptions = [{value: "Marke", label: "Marke"}, {value: "Produktart", label: "Produktart"},
      {value: "Breite", label: "Breite"}, {value: "Höhe", label: "Höhe"}, {value: "Länge", label: "Länge"},
      {value: "Farbe", label: "Farbe"}, {value: "Größe", label: "Größe"},
      {value: "Material", label: "Material"}, {value: "Polsterstoff", label: "Polsterstoff"},
      {value: "Anzahl der Teile", label: "Anzahl der Teile"}, {value: "Anzahl der Schubladen", label: "Anzahl der Schubladen"},
      {value: "Anzahl der Regalfächer", label: "Anzahl der Regalfächer"},
      {value: "Set enthält", label: "Set enthält"}, {value: "Liegefläche", label: "Liegefläche"},
      {value: "Gestell", label: "Gestell"}, {value: "Herstellernummer", label: "Herstellernummer"}];
    const [eBayID1, setEBayID1] = useState<string>("");
    const [eBayID2, setEBayID2] = useState<string | null>("");
    const [selectedFieldNames, setSelectedFieldNames] = useState<{
      value: string | number;
      label: string
    }[]>([]);
    const getListingCreatorFields = async () => {
      await axios.get(`${backendUrl}/admin_panel/get_listing_creator_fields/`, config)
        .then(response => {
          setListingCreatorFields(response.data);
        })
        .catch(error => alert("Fehler beim Laden der Felder des Listing Creators " + error.toString()))
    };

    const handleAddClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setErrorMessage("");
      setInfoMessage("");

      if (!eBayID1 || !selectedFieldNames) {
        setErrorMessage("Alle Felder müssen ausgefüllt sein!");
        return;
      } else if (selectedFieldNames.length == 0) {
        setErrorMessage("Es muss mindestens ein Feld ausgewählt sein!");
        return;
      }

      const data = {
        ebay_category_id_1: Number(eBayID1),
        ebay_category_id_2: eBayID2? Number(eBayID2): null,
        fields: selectedFieldNames.map(f => f.value)
      };
      await axios
        .post(
          `${backendUrl}/admin_panel/add_listing_creator_fields/`,
          data,
          config
        )
        .then(() => {
          setInfoMessage("Das Hinzufügen der Zuordnung war erfolgreich.");
          getListingCreatorFields()
          setEBayID1("");
          setEBayID2("");
          setSelectedFieldNames([]);
        })
        .catch((err) => {
          const error_str = (!err.response) ? "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar." // This will handle network errors or cases where the server doesn't respond at all
            : (err.response.status === 500) ? "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan)."
              : err.response.data?.detail?.toString();
          setErrorMessage(error_str);
        });
    };

    const handleDeleteClick = async (event: React.MouseEvent<HTMLButtonElement>, data) => {
      event.preventDefault();
      setErrorMessage("");

      await axios
        .delete(`${backendUrl}/admin_panel/remove_listing_creator_fields/`, {
          ...config,
          data: data
        })
        .then(() => {
          getListingCreatorFields();
          setInfoMessage("Eintrag erfolgreich gelöscht.");
        })
        .catch((err) => {
          const error_str = (!err.response)
            ? "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar."
            : (err.response.status === 500)
              ? "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan)."
              : err.response.data?.detail?.toString();
          setErrorMessage(error_str);
        });
    }

    return <div style={{display: 'flex', flexDirection: 'column', gap: "15px", alignItems: 'center'}}>
      {renderScrollDownButton(ListingCreator_ref)}
      <div style={{display: "flex", flexDirection: "row", justifyContent: "center"}}>
        <table style={{width: "100%", borderCollapse: "collapse", marginTop: "10px"}}>
          <thead>
          <tr style={{borderBottom: "2px solid #ccc", textAlign: "center"}}>
            <th style={{padding: "10px", width: "25%"}}>eBay-ID1</th>
            <th style={{padding: "10px", width: "25%"}}>eBay-ID2</th>
            <th style={{padding: "10px", width: "50%"}}>Felder</th>
            <th style={{padding: "10px", width: "5%"}}></th>
          </tr>
          </thead>
          <tbody>
          {listingCreatorFields && Object.entries(listingCreatorFields).length > 0 && listingCreatorFields.map((entry, idx) => (
            <tr key={idx} style={{borderBottom: "1px solid #eee"}}>
              <td style={{padding: "10px"}}>{entry.ebay_category_id_1}</td>
              <td style={{padding: "10px"}}>{entry.ebay_category_id_2}</td>
              <td style={{padding: "10px"}}>
                <SearchableMultiSelect items={fieldOptions} inputLabel="Märkte" selectAllOption={true}
                                       selectAllInitially={false} disabled={true}
                                       value={entry.fields.map(field => ({value: field, label: field}))} setValue={() => {}}/>
              </td>
              <td style={{padding: "10px"}}>
                <DefaultButton
                  label="-"
                  color={"red"}
                  variant={"contained"}
                  size="small"
                  sx={{minWidth: "30px", width: "30px", height: "30px"}}
                  onClick={async (e) => {
                    await handleDeleteClick(e, {ebay_category_id_1: entry.ebay_category_id_1, ebay_category_id_2: entry.ebay_category_id_2})
                  }}
                />
              </td>
            </tr>
          ))}
          </tbody>
        </table>
      </div>
      <div style={{display: 'flex', flexDirection: 'row', gap: "15px", justifyContent: "center", width: "75%"}}>
        <CustomTextField label={"eBay Kategorie 1"} value={eBayID1} onChange={(e) => setEBayID1(e.target.value)} inputMode={"integer"} is_required={true} sx={{width: "25%"}}/>
        <CustomTextField label={"eBay Kategorie 2"} value={eBayID2} onChange={(e) => setEBayID2(e.target.value)} inputMode={"integer"} is_required={false} sx={{width: "25%"}} ref={ListingCreator_ref}/>
        <SearchableMultiSelect items={fieldOptions} inputLabel="Felder" selectAllOption={true}
                               selectAllInitially={false}
                               value={selectedFieldNames}
                               setValue={(newVal) => setSelectedFieldNames(newVal)}/>
      </div>
      <div style={{display: 'flex', flexDirection: 'column', gap: "15px", alignItems: 'center', width: '100%'}}
           ref={ListingCreator_ref}>
        <SubmitButton label="Verknüpfung hinzufügen" size="small" onClick={handleAddClick} sx={{width: "50%"}}/>
        {renderErrorMessage(errorMessage)}
        {renderInfoMessage(infoMessage)}
      </div>
    </div>
  };
  // Tab 8
  const RenderOrderProcessingPanel = () => {
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [infoMessage, setInfoMessage] = useState<string | null>(null);
    const [countryID, setCountryID] = useState<string>("");
    const [countryName, setCountryName] = useState<string>("");
    const [backofficeCountryID, setBackofficeCountryID] = useState<string>("");
    const [backofficeCountryName, setBackofficeCountryName] = useState<string>("");
    const [supplierID, setSupplierID] = useState<string>("");
    const [supplierMail, setSupplierMail] = useState<string>("");

    const getActiveCountries = async () => {
      await axios.get(`${backendUrl}/admin_panel/get_active_countries/`, config)
        .then(response => {
          setActiveCountries(response.data);
        })
        .catch(error => alert("Fehler beim Laden der Aktiven Länder " + error.toString()))
    };

    const handleAddActiveCountryClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setErrorMessage("");
      setInfoMessage("");

      if (!countryID || !countryName) {
        setErrorMessage("Beide Felder müssen ausgefüllt sein!");
        return;
      }

      const data = {
        id: Number(countryID),
        name: countryName
      };
      await axios
        .post(
          `${backendUrl}/admin_panel/add_active_country/`,
          data,
          config
        )
        .then(() => {
          setInfoMessage("Das Hinzufügen der Zuordnung war erfolgreich.");
          getActiveCountries();
          setCountryID("");
          setCountryName("");
        })
        .catch((err) => {
          const error_str = (!err.response) ? "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar." // This will handle network errors or cases where the server doesn't respond at all
            : (err.response.status === 500) ? "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan)."
              : err.response.data?.detail?.toString();
          setErrorMessage(error_str);
        });
    };

    const handleDeleteActiveCountryClick = async (event: React.MouseEvent<HTMLButtonElement>, data) => {
      event.preventDefault();
      setErrorMessage("");

      await axios
        .delete(`${backendUrl}/admin_panel/remove_active_country/`, {
          ...config,
          data: data
        })
        .then(() => {
          getActiveCountries();
          setInfoMessage("Eintrag erfolgreich gelöscht.");
        })
        .catch((err) => {
          const error_str = (!err.response)
            ? "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar."
            : (err.response.status === 500)
              ? "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan)."
              : err.response.data?.detail?.toString();
          setErrorMessage(error_str);
        });
    }

    const getBackofficeActiveCountries = async () => {
      await axios.get(`${backendUrl}/admin_panel/get_backoffice_active_countries/`, config)
        .then(response => {
          setBackofficeActiveCountries(response.data);
        })
        .catch(error => alert("Fehler beim Laden der Aktiven (Backoffice) Länder " + error.toString()))
    };

    const handleAddBackofficeActiveCountryClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setErrorMessage("");
      setInfoMessage("");

      if (!backofficeCountryID || !backofficeCountryName) {
        setErrorMessage("Beide Felder müssen ausgefüllt sein!");
        return;
      }

      const data = {
        id: Number(backofficeCountryID),
        name: backofficeCountryName
      };
      await axios
        .post(
          `${backendUrl}/admin_panel/add_backoffice_active_country/`,
          data,
          config
        )
        .then(() => {
          setInfoMessage("Das Hinzufügen der Zuordnung war erfolgreich.");
          getBackofficeActiveCountries();
          setBackofficeCountryID("");
          setBackofficeCountryName("");
        })
        .catch((err) => {
          const error_str = (!err.response) ? "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar." // This will handle network errors or cases where the server doesn't respond at all
            : (err.response.status === 500) ? "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan)."
              : err.response.data?.detail?.toString();
          setErrorMessage(error_str);
        });
    };

    const handleDeleteBackofficeActiveCountryClick = async (event: React.MouseEvent<HTMLButtonElement>, data) => {
      event.preventDefault();
      setErrorMessage("");

      await axios
        .delete(`${backendUrl}/admin_panel/remove_backoffice_active_country/`, {
          ...config,
          data: data
        })
        .then(() => {
          getBackofficeActiveCountries();
          setInfoMessage("Eintrag erfolgreich gelöscht.");
        })
        .catch((err) => {
          const error_str = (!err.response)
            ? "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar."
            : (err.response.status === 500)
              ? "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan)."
              : err.response.data?.detail?.toString();
          setErrorMessage(error_str);
        });
    }

    const getMissingMails = async () => {
      await axios.get(`${backendUrl}/admin_panel/get_missing_mails/`, config)
        .then(response => {
          setMissingMails(response.data);
        })
        .catch(error => alert("Fehler beim Laden der Mails " + error.toString()))
    };

    const handleAddMissingMailClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setErrorMessage("");
      setInfoMessage("");

      if (!supplierID || !supplierMail) {
        setErrorMessage("Beide Felder müssen ausgefüllt sein!");
        return;
      }

      const data = {
        id: Number(supplierID),
        name: supplierMail
      };
      await axios
        .post(
          `${backendUrl}/admin_panel/add_missing_mail/`,
          data,
          config
        )
        .then(() => {
          setInfoMessage("Das Hinzufügen der Zuordnung war erfolgreich.");
          getMissingMails();
          setSupplierID("");
          setSupplierMail("");
        })
        .catch((err) => {
          const error_str = (!err.response) ? "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar." // This will handle network errors or cases where the server doesn't respond at all
            : (err.response.status === 500) ? "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan)."
              : err.response.data?.detail?.toString();
          setErrorMessage(error_str);
        });
    };

    const handleDeleteMissingMailClick = async (event: React.MouseEvent<HTMLButtonElement>, data) => {
      event.preventDefault();
      setErrorMessage("");

      await axios
        .delete(`${backendUrl}/admin_panel/remove_missing_mail/`, {
          ...config,
          data: data
        })
        .then(() => {
          getMissingMails();
          setInfoMessage("Eintrag erfolgreich gelöscht.");
        })
        .catch((err) => {
          const error_str = (!err.response)
            ? "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar."
            : (err.response.status === 500)
              ? "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan)."
              : err.response.data?.detail?.toString();
          setErrorMessage(error_str);
        });
    }


    return <div style={{display: 'flex', flexDirection: 'column', gap: "25px", alignItems: 'center'}}>
      {/* Aktive Länder */}
      <div style={{display: 'flex', flexDirection: 'column', gap: "15px", alignItems: 'center', width: "80%"}}>
        <p>Aktive Länder</p>
        {renderScrollDownButton(AktiveLaender_ref)}
        <div style={{display: "flex", flexDirection: "row", justifyContent: "center", width: "30%"}}>
          <table style={{width: "100%", borderCollapse: "collapse", marginTop: "10px"}}>
            <thead>
            <tr style={{borderBottom: "2px solid #ccc", textAlign: "center", width: "80%"}}>
              <th style={{padding: "10px", width: "20%"}}>ID</th>
              <th style={{padding: "10px", minWidth: "50%"}}>Name</th>
              <th style={{padding: "10px"}}></th>
            </tr>
            </thead>
            <tbody>
            {activeCountries && activeCountries.length > 0 && activeCountries.map(([id, name], idx) => (
              <tr key={idx} style={{borderBottom: "1px solid #eee"}}>
                <td style={{padding: "10px"}}>{id}</td>
                <td style={{padding: "10px"}}>{name}</td>
                <td style={{padding: "10px"}}>
                  <DefaultButton
                    label="-"
                    color={"red"}
                    variant={"contained"}
                    size="small"
                    sx={{minWidth: "30px", width: "30px", height: "30px"}}
                    onClick={async (e) => {
                      await handleDeleteActiveCountryClick(e, {id: id})
                    }}
                  />
                </td>
              </tr>
            ))}
            </tbody>
          </table>
        </div>
        <div style={{display: 'flex', flexDirection: 'row', gap: "15px", justifyContent: "center"}}>
          <CustomTextField label={"Land ID"} value={countryID} onChange={(e) => setCountryID(e.target.value)}
                           inputMode={"integer"} is_required={true} sx={{width: "25%"}}/>
          <CustomTextField label={"Land Name"} value={countryName} onChange={(e) => setCountryName(e.target.value)}
                           inputMode={"text"} is_required={true} sx={{width: "40%"}} ref={AktiveLaender_ref}/>
        </div>
        <SubmitButton label="Land hinzufügen" size="small" onClick={handleAddActiveCountryClick} sx={{width: "50%"}}/>
        {renderErrorMessage(errorMessage)}
        {renderInfoMessage(infoMessage)}
      </div>
      <hr style={{width: "80%", border: "0", borderTop: "2px solid #eee", margin: "10px 0"}}/>
      {/* Backoffice Aktive Länder */}
      <div style={{display: 'flex', flexDirection: 'column', gap: "15px", alignItems: 'center', width: "80%"}}>
        <p>Aktive Länder im Backoffice</p>
        {renderScrollDownButton(BackofficeAktiveLaender_ref)}
        <div style={{display: "flex", flexDirection: "row", justifyContent: "center", width: "30%"}}>
          <table style={{width: "100%", borderCollapse: "collapse", marginTop: "10px"}}>
            <thead>
            <tr style={{borderBottom: "2px solid #ccc", textAlign: "center", width: "80%"}}>
              <th style={{padding: "10px", width: "20%"}}>ID</th>
              <th style={{padding: "10px", minWidth: "50%"}}>Name</th>
              <th style={{padding: "10px"}}></th>
            </tr>
            </thead>
            <tbody>
            {backofficeActiveCountries && backofficeActiveCountries.length > 0 && backofficeActiveCountries.map(([id, name], idx) => (
              <tr key={idx} style={{borderBottom: "1px solid #eee"}}>
                <td style={{padding: "10px"}}>{id}</td>
                <td style={{padding: "10px"}}>{name}</td>
                <td style={{padding: "10px"}}>
                  <DefaultButton
                    label="-"
                    color={"red"}
                    variant={"contained"}
                    size="small"
                    sx={{minWidth: "30px", width: "30px", height: "30px"}}
                    onClick={async (e) => {
                      await handleDeleteBackofficeActiveCountryClick(e, {id: id})
                    }}
                  />
                </td>
              </tr>
            ))}
            </tbody>
          </table>
        </div>
        <div style={{display: 'flex', flexDirection: 'row', gap: "15px", justifyContent: "center"}}>
          <CustomTextField label={"Backoffice Land ID"} value={backofficeCountryID}
                           onChange={(e) => setBackofficeCountryID(e.target.value)}
                           inputMode={"integer"} is_required={true} sx={{width: "50%"}}/>
          <CustomTextField label={"Backoffice Land Name"} value={backofficeCountryName}
                           onChange={(e) => setBackofficeCountryName(e.target.value)}
                           inputMode={"text"} is_required={true} sx={{width: "50%"}} ref={BackofficeAktiveLaender_ref}/>
        </div>
        <SubmitButton label="Backoffice Land hinzufügen" size="small" onClick={handleAddBackofficeActiveCountryClick}
                      sx={{width: "50%"}}/>
        {renderErrorMessage(errorMessage)}
        {renderInfoMessage(infoMessage)}
      </div>
      <hr style={{width: "80%", border: "0", borderTop: "2px solid #eee", margin: "10px 0"}}/>
      {/* Missing Mails */}
      <div style={{display: 'flex', flexDirection: 'column', gap: "15px", alignItems: 'center', width: "80%"}}>
        <p>Fehlende Mails sind wichtig, da nicht alle Mail-Addressen von Zulieferern in PlentyMarkets vorhanden sind.
          Falls Plenty diese nicht speichert, wird auf diese Datenbank zurückgegriffen.</p>
        {renderScrollDownButton(MissingMails_ref)}
        <div style={{display: "flex", flexDirection: "row", justifyContent: "center", width: "50%"}}>
          <table style={{width: "100%", borderCollapse: "collapse", marginTop: "10px"}}>
            <thead>
            <tr style={{borderBottom: "2px solid #ccc", textAlign: "center", width: "80%"}}>
              <th style={{padding: "10px", width: "25%"}}>Lieferanten-ID</th>
              <th style={{padding: "10px", minWidth: "50%"}}>E-Mail</th>
              <th style={{padding: "10px"}}></th>
            </tr>
            </thead>
            <tbody>
            {missingMails && missingMails.length > 0 && missingMails.map(([id, name], idx) => (
              <tr key={idx} style={{borderBottom: "1px solid #eee"}}>
                <td style={{padding: "10px"}}>{id}</td>
                <td style={{padding: "10px"}}>{name}</td>
                <td style={{padding: "10px"}}>
                  <DefaultButton
                    label="-"
                    color={"red"}
                    variant={"contained"}
                    size="small"
                    sx={{minWidth: "30px", width: "30px", height: "30px"}}
                    onClick={async (e) => {
                      await handleDeleteMissingMailClick(e, {id: id})
                    }}
                  />
                </td>
              </tr>
            ))}
            </tbody>
          </table>
        </div>
        <div style={{display: 'flex', flexDirection: 'row', gap: "15px", justifyContent: "center"}}>
          <CustomTextField label={"Lieferanten-ID"} value={supplierID} onChange={(e) => setSupplierID(e.target.value)}
                           inputMode={"integer"} is_required={true} sx={{width: "30%"}}/>
          <CustomTextField label={"E-Mail"} value={supplierMail} onChange={(e) => setSupplierMail(e.target.value)}
                           inputMode={"text"} is_required={true} sx={{width: "50%"}} ref={MissingMails_ref}/>
        </div>
        <SubmitButton label="E-Mail hinzufügen" size="small" onClick={handleAddMissingMailClick} sx={{width: "50%"}}/>
        {renderErrorMessage(errorMessage)}
        {renderInfoMessage(infoMessage)}
      </div>
      <hr style={{width: "80%", border: "0", borderTop: "2px solid #eee", margin: "10px 0"}}/>
    </div>
  };


  const tabStyle = {
    '&.Mui-selected': {
      color: 'var(--naturGreen)',
    }
  };
  return (
    <>
      <Navbar showWarningWhenClicked={true}/>
      <Helmet>
        <title>{"Admin Panel"}</title>
      </Helmet>
      <div className="AdminPanelPage">
        <Tabs
          value={selectedTab}
          onChange={(_, newVal) => {
            setSelectedTab(newVal);
          }}
          variant="scrollable"
          scrollButtons
          allowScrollButtonsMobile
          aria-label="scrollable force tabs example"
          TabIndicatorProps={{style: {backgroundColor: 'var(--naturGreen)'}}}
        >
          <Tab label="Benutzer & Rechte" id="tab-0" aria-controls="tab-0" sx={tabStyle}/>
          <Tab label="Plenty-Kategorien" id="tab-1" aria-controls="tab-1" sx={tabStyle}/>
          <Tab label="Ebay-, Otto- & Hood-Kategorien" id="tab-2" aria-controls="tab-2" sx={tabStyle}/>
          <Tab label="Farbe, Holzart & Material" id="tab-3" aria-controls="tab-3" sx={tabStyle}/>
          <Tab label="Verbotene Worte" id="tab-4" aria-controls="tab-4" sx={tabStyle}/>
          <Tab label="Hersteller-Lieferant" id="tab-5" aria-controls="tab-5" sx={tabStyle}/>
          <Tab label="Marktverfügbarkeitsbeschränkungen" id="tab-6" aria-controls="tab-6" sx={tabStyle}/>
          <Tab label="eBay-ID -> Standard Felder (Listing-App)" id="tab-7" aria-controls="tab-7" sx={tabStyle}/>
          <Tab label="Aktive Länder, Aktive Backoffice Länder, fehlende E-Mails (Auftragsbearbeitung)" id="tab-8"
               aria-controls="tab-8" sx={tabStyle}/>
        </Tabs>
        <CustomTabPanel value={selectedTab} index={0}>{RenderUserPanel()}</CustomTabPanel>
        <CustomTabPanel value={selectedTab} index={1}>{RenderManageCategoriesPanel(fetchData)}</CustomTabPanel>
        <CustomTabPanel value={selectedTab} index={2}>{RenderMarketplaceCategoriesPanel()}</CustomTabPanel>
        <CustomTabPanel value={selectedTab} index={3}>{RenderMaterialManagementPanel()}</CustomTabPanel>
        <CustomTabPanel value={selectedTab} index={4}>{RenderInvalidWordPanel()}</CustomTabPanel>
        <CustomTabPanel value={selectedTab} index={5}>{RenderManufacturerSupplierPanel()}</CustomTabPanel>
        <CustomTabPanel value={selectedTab} index={6}>{RenderManufacturerAvailabilityConstraintPanel()}</CustomTabPanel>
        <CustomTabPanel value={selectedTab} index={7}>{RenderListingCreatorFieldPanel()}</CustomTabPanel>
        <CustomTabPanel value={selectedTab} index={8}>{RenderOrderProcessingPanel()}</CustomTabPanel>
        {/*<CustomTabPanel value={selectedTab} index={}>{}</CustomTabPanel>*/}
      </div>
      {ColorModeIcon()}
    </>
  );
}

export default AdminPanel;
